class TpsFieldDateA11y {
  static ensureControlId(control, fallback = 'tps-field') {
    if (control.id && document.getElementById(control.id) === control) return control.id;
    TpsFieldDateA11y.instanceCount = (TpsFieldDateA11y.instanceCount || 0) + 1;
    let identifier = `${control.id || control.name || fallback}-${TpsFieldDateA11y.instanceCount}`;
    while (document.getElementById(identifier)) {
      TpsFieldDateA11y.instanceCount += 1;
      identifier = `${control.id || control.name || fallback}-${TpsFieldDateA11y.instanceCount}`;
    }
    control.id = identifier;
    return identifier;
  }

  static setFieldErrorState(control, errorId, invalid) {
    const descriptions = new Set((control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    if (invalid) {
      descriptions.add(errorId);
    } else {
      descriptions.delete(errorId);
    }
    if (descriptions.size) {
      control.setAttribute('aria-describedby', [...descriptions].join(' '));
    } else {
      control.removeAttribute('aria-describedby');
    }
    control.setAttribute('aria-invalid', `${invalid}`);
  }

  static clearFieldError(control, errorId = `${control.id || control.name}-error`) {
    document.getElementById(errorId)?.remove();
    TpsFieldDateA11y.setFieldErrorState(control, errorId, false);
  }

  static showFieldError(control, field, text, options = {}) {
    const identifier = TpsFieldDateA11y.ensureControlId(control);
    const errorId = options.errorId || `${identifier}-error`;
    TpsFieldDateA11y.clearFieldError(control, errorId);
    const error = document.createElement('div');
    error.id = errorId;
    error.className = options.className || 'variant-error-tip';
    error.setAttribute('role', 'alert');
    if (options.allowHtml) {
      error.innerHTML = text;
    } else {
      error.textContent = text;
    }
    if (options.prepend) {
      field.prepend(error);
    } else {
      field.appendChild(error);
    }
    TpsFieldDateA11y.setFieldErrorState(control, errorId, true);
    return { error, errorId };
  }

  constructor(host) {
    this.host = host;
    this.input = host.querySelector('input');
    this.field = host.closest('div');
    TpsFieldDateA11y.instanceCount = (TpsFieldDateA11y.instanceCount || 0) + 1;
    const baseIdentifier = this.input.id || this.input.name || 'tps-field-date';
    let identifier = baseIdentifier;
    if (!this.input.id || document.getElementById(this.input.id) !== this.input) {
      identifier = `${baseIdentifier}-${TpsFieldDateA11y.instanceCount}`;
      while (document.getElementById(identifier)) {
        TpsFieldDateA11y.instanceCount += 1;
        identifier = `${baseIdentifier}-${TpsFieldDateA11y.instanceCount}`;
      }
      this.input.id = identifier;
    }
    this.calendarId = `${identifier}-calendar`;
    this.errorId = `${identifier}-error`;
    this.error = null;
    this.monthControlHandlers = [];
  }
  connectPicker(picker) {
    const calendar = picker.calendarContainer;
    if (!calendar) return;
    this.setExpanded(false);
    calendar.id = this.calendarId;
    calendar.classList.add('tps-field-date-calendar');
    calendar.setAttribute('role', 'dialog');
    calendar.setAttribute('aria-label', `${this.input.getAttribute('aria-label')} calendar`);
    this.input.setAttribute('aria-controls', this.calendarId);
    this.enhanceMonthControl(picker.prevMonthNav, 'Previous month');
    this.enhanceMonthControl(picker.nextMonthNav, 'Next month');
  }

  enhanceMonthControl(control, label) {
    if (!control) return;
    control.setAttribute('role', 'button');
    control.setAttribute('aria-label', label);
    const handleKeydown = (event) => {
      if (!['Enter', ' '].includes(event.key) || control.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      event.stopPropagation();
      control.click();
    };
    control.addEventListener('keydown', handleKeydown);
    this.monthControlHandlers.push({ control, handleKeydown });
  }
  setExpanded(expanded) {
    this.input.setAttribute('aria-expanded', `${expanded}`);
  }
  showError(text) {
    this.clearError();
    this.error = document.createElement('div');
    this.error.id = this.errorId;
    this.error.className = 'variant-error-tip';
    this.error.setAttribute('role', 'alert');
    this.error.textContent = text;
    this.field.appendChild(this.error);
    const descriptions = new Set((this.input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    descriptions.add(this.errorId);
    this.input.setAttribute('aria-describedby', [...descriptions].join(' '));
    this.input.setAttribute('aria-invalid', 'true');
  }
  clearError() {
    this.error?.remove();
    this.error = null;
    const descriptions = (this.input.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((id) => id && id !== this.errorId);
    if (descriptions.length) {
      this.input.setAttribute('aria-describedby', descriptions.join(' '));
    } else {
      this.input.removeAttribute('aria-describedby');
    }
    this.input.setAttribute('aria-invalid', 'false');
  }
  destroy() {
    this.setExpanded(false);
    for (const { control, handleKeydown } of this.monthControlHandlers) {
      control.removeEventListener('keydown', handleKeydown);
    }
    this.monthControlHandlers = [];
  }
}

class TpsProductQuestionListA11y {
  constructor(host, onRequestClose) {
    this.host = host;
    this.root = host.shadowRoot;
    this.popup = this.root.querySelector('.popup');
    this.overlay = this.root.querySelector('.overlay');
    this.closeButton = this.root.querySelector('.close-btn');
    this.onRequestClose = onRequestClose;
    this.currentTrigger = null;
    this.isOpen = false;
    this.ownsScrollLock = false;
    this.focusFrame = null;
    this.handleKeydown = this.handleKeydown.bind(this);
    this.prepareDialog();
  }

  prepareDialog() {
    if (!this.popup) return;
    this.popup.setAttribute('role', 'dialog');
    this.popup.setAttribute('aria-modal', 'true');
    this.popup.setAttribute('aria-labelledby', 'main-title');
    this.popup.setAttribute('tabindex', '-1');
    this.popup.setAttribute('aria-hidden', 'true');
    this.popup.setAttribute('inert', '');
    this.overlay?.setAttribute('aria-hidden', 'true');
    if (this.closeButton) {
      this.closeButton.type = 'button';
      this.closeButton.setAttribute('aria-label', 'Close');
    }
  }

  getFocusableElements() {
    if (!this.popup) return [];
    const selector = [
      'button:not([disabled]):not([tabindex="-1"])',
      'a[href]:not([tabindex="-1"])',
      'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      '[contenteditable="true"]:not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    return Array.from(this.popup.querySelectorAll(selector)).filter((element) => {
      if (element.closest('[inert], [aria-hidden="true"]')) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    });
  }

  open(trigger) {
    if (!this.popup || this.isOpen) return;
    this.currentTrigger = trigger || document.activeElement;
    this.currentTrigger?.setAttribute('aria-haspopup', 'dialog');
    this.currentTrigger?.setAttribute('aria-expanded', 'true');
    this.popup.removeAttribute('inert');
    this.popup.setAttribute('aria-hidden', 'false');
    this.isOpen = true;
    this.lockScroll();
    document.addEventListener('keydown', this.handleKeydown, true);
    this.focusFrame = requestAnimationFrame(() => {
      this.focusFrame = null;
      this.closeButton?.focus({ preventScroll: true });
    });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.popup) return;
    if (this.focusFrame) {
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = null;
    }
    document.removeEventListener('keydown', this.handleKeydown, true);
    const trigger = this.currentTrigger;
    this.isOpen = false;
    if (restoreFocus && trigger?.isConnected) {
      trigger.focus({ preventScroll: true });
    }
    trigger?.setAttribute('aria-expanded', 'false');
    this.popup.setAttribute('inert', '');
    this.popup.setAttribute('aria-hidden', 'true');
    this.unlockScroll();
    this.currentTrigger = null;
  }

  handleKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.onRequestClose?.();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusableElements = this.getFocusableElements();
    if (!focusableElements.length) {
      event.preventDefault();
      this.popup.focus({ preventScroll: true });
      return;
    }

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const activeElement = this.root.activeElement;
    if (!this.popup.contains(activeElement)) {
      event.preventDefault();
      firstFocusable.focus();
    } else if (event.shiftKey && activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
    } else if (!event.shiftKey && activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  }

  lockScroll() {
    this.ownsScrollLock = !document.documentElement.classList.contains('lock-scroll');
    document.documentElement.classList.add('lock-scroll');
  }

  unlockScroll() {
    if (this.ownsScrollLock) {
      document.documentElement.classList.remove('lock-scroll');
    }
    this.ownsScrollLock = false;
  }

  destroy() {
    this.close({ restoreFocus: false });
  }
}

class TpsSidePopupModal extends HTMLElement {
  constructor() {
    super();
    this.templateId = this.getAttribute('template-id') || 'tps-side-popup-template';
    this.overlay = null;
    this.popup = null;
    this.titleNode = null;
    this.contentNode = null;
    this.hideTimer = null;
    this.lastTrigger = null;
    this.handleKeydown = this.handleKeydown.bind(this);
  }
  static getSharedInstance() {
    const existed = document.querySelector('tps-side-popup-modal');
    if (existed) return existed;
    const instance = document.createElement('tps-side-popup-modal');
    document.body.appendChild(instance);
    return instance;
  }
  connectedCallback() {
    this.ensureMounted();
  }
  ensureMounted() {
    if (this.isConnected && this.popup && this.overlay && this.contentNode && this.titleNode) {
      return true;
    }
    const template = document.getElementById(this.templateId);
    if (!template) {
      console.error(`[tps-side-popup] Missing template: ${this.templateId}`);
      return false;
    }
    const fragment = template.content.cloneNode(true);
    this.innerHTML = '';
    this.appendChild(fragment);
    this.overlay = this.querySelector('[data-side-popup-overlay]');
    this.popup = this.querySelector('[data-side-popup]');
    this.titleNode = this.querySelector('#tps-side-popup-title');
    this.contentNode = this.querySelector('[data-side-popup-content]');
    const closeButtons = this.querySelectorAll('[data-side-popup-close]');
    if (!this.overlay || !this.popup || !this.titleNode || !this.contentNode) {
      console.error('[tps-side-popup] Invalid template structure');
      return false;
    }
    this.overlay.addEventListener('click', () => this.close());
    closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
    return true;
  }
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }
  render(payload = {}) {
    const title = `${payload?.title || ''}`.trim() || 'Details';
    const sections = Array.isArray(payload?.sections) ? payload.sections : [];
    this.titleNode.textContent = title;
    this.contentNode.innerHTML = '';
    sections.forEach((section) => {
      const sectionEl = document.createElement('section');
      sectionEl.className = 'tps-side-popup__section';
      const sectionTitle = `${section?.title || ''}`.trim();
      if (sectionTitle) {
        const titleEl = document.createElement('h4');
        titleEl.className = 'tps-side-popup__section-title';
        titleEl.textContent = sectionTitle;
        sectionEl.appendChild(titleEl);
      }
      const bullets = Array.isArray(section?.bullets) ? section.bullets : [];
      if (bullets.length) {
        const listEl = document.createElement('ul');
        listEl.className = 'tps-side-popup__list';
        bullets.forEach((item) => {
          const li = document.createElement('li');
          li.textContent = `${item || ''}`.trim();
          if (li.textContent) {
            listEl.appendChild(li);
          }
        });
        if (listEl.childElementCount > 0) {
          sectionEl.appendChild(listEl);
        }
      }
      const paragraphs = Array.isArray(section?.paragraphs) ? section.paragraphs : [];
      paragraphs.forEach((text) => {
        const content = `${text || ''}`.trim();
        if (!content) return;
        const paragraph = document.createElement('p');
        paragraph.className = 'tps-side-popup__answer';
        paragraph.textContent = content;
        sectionEl.appendChild(paragraph);
      });
      const faq = Array.isArray(section?.faq) ? section.faq : [];
      faq.forEach((item) => {
        const question = `${item?.question || ''}`.trim();
        const answer = `${item?.answer || ''}`.trim();
        if (!question && !answer) return;
        const faqItem = document.createElement('div');
        faqItem.className = 'tps-side-popup__faq-item';
        if (question) {
          const questionEl = document.createElement('p');
          questionEl.className = 'tps-side-popup__question';
          questionEl.textContent = question;
          faqItem.appendChild(questionEl);
        }
        if (answer) {
          const answerEl = document.createElement('p');
          answerEl.className = 'tps-side-popup__answer';
          answerEl.textContent = answer;
          faqItem.appendChild(answerEl);
        }
        sectionEl.appendChild(faqItem);
      });
      if (sectionEl.childElementCount > 0) {
        this.contentNode.appendChild(sectionEl);
      }
    });
  }
  open(payload = {}, trigger = null) {
    if (!this.ensureMounted()) return;
    this.render(payload);
    if (this.hideTimer) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.lastTrigger = trigger || document.activeElement;
    this.overlay.hidden = false;
    this.popup.hidden = false;
    requestAnimationFrame(() => {
      this.overlay.classList.add('is-active');
      this.popup.classList.add('is-active');
      this.popup.focus();
    });
    document.addEventListener('keydown', this.handleKeydown);
  }
  close({ restoreFocus = true } = {}) {
    if (!this.popup || !this.overlay) return;
    if (this.hideTimer) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.overlay.classList.remove('is-active');
    this.popup.classList.remove('is-active');
    this.hideTimer = window.setTimeout(() => {
      if (!this.popup.classList.contains('is-active')) {
        this.overlay.hidden = true;
        this.popup.hidden = true;
      }
    }, 320);
    document.removeEventListener('keydown', this.handleKeydown);
    if (restoreFocus && this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
      this.lastTrigger.focus();
    }
  }
}
if (!customElements.get('tps-side-popup-modal')) {
  customElements.define('tps-side-popup-modal', TpsSidePopupModal);
}

class TpsProductOptionLinkage {
  static create(product) {
    const handle = product._wrapper.querySelector('.tps-variant-picker-extra')?.dataset.productHandle;
    const rules = [
      {
        handle: 'scandina-sheer-room-divider-curtain-moven-u-shaped-track',
        sources: ['o-ddl-02', 'o-ddl-04', 'o-ddl-03'],
        target: 'o-ddl-06'
      },
      {
        handle: 'scandina-sheer-room-divider-curtain-moven-l-shaped-track',
        sources: ['opt_1791971813808_2', 'opt_1791971813814_3'],
        target: 'opt_1791971813828_5'
      },
      {
        handle: 'liz-linen-room-divider-curtain-moven-u-shaped-track',
        sources: ['o-ddl-02', 'o-ddl-04', 'o-ddl-03'],
        target: 'o-ddl-06'
      },
      {
        handle: 'liz-linen-room-divider-curtain-moven-l-shaped-track',
        sources: ['opt_1791971813808_2', 'opt_1791971813814_3'],
        target: 'opt_1791971813828_5'
      },
      {
        handle: 'moven-suspended-room-divider-curtain-track',
        variantId: '53398690496823',
        sources: ['o-ddl-02', 'o-ddl-03'],
        target: 'o-ddl-04'
      },
      {
        handle: 'moven-suspended-room-divider-curtain-track',
        variantId: '53398690529591',
        sources: ['o-ddl-06', 'o-ddl-07', 'o-ddl-08'],
        target: 'o-ddl-09'
      }
    ].filter(rule => rule.handle === handle);
    return rules.length ? new TpsProductOptionLinkage(product, rules) : null;
  }
  constructor(product, rules) {
    this.product = product;
    this.rules = rules;
    this.abortController = new AbortController();
    this.invalidField = null;
    this.controls = rules.map(rule => {
      const fieldset = product._wrapper.querySelector(`fieldset[data-key="${rule.target}"]`);
      const select = fieldset?.querySelector('tps-field-select');
      if (!select) return null;
      const result = document.createElement('div');
      result.className = 'ctm-optlink-result';
      result.hidden = true;
      select.after(result);
      return {
        rule,
        fieldset,
        select,
        result,
        hidden: select.hidden,
        display: select.style.display,
        ariaHidden: select.getAttribute('aria-hidden'),
        tabIndex: select.input.getAttribute('tabindex')
      };
    }).filter(Boolean);
    const { signal } = this.abortController;
    product.addToCartForm?.addEventListener('submit', (event) => {
      if (!this.invalidField) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.invalidField.setAttribute('tabindex', '-1');
      this.invalidField.focus();
    }, { capture: true, signal });
    window.addEventListener('pageshow', () => product.updateField(), { signal });
  }
  lengthInFeet(label = '') {
    const match = label.match(/^\s*(\d+(?:\.\d+)?)\s*ft\b/i);
    return match ? Number(match[1]) : NaN;
  }
  sync() {
    const variantId = String(this.product.defaultOptions.id);
    this.invalidField = null;
    let changed = false;
    for (const { rule, fieldset, select, result } of this.controls) {
      const target = this.product.fieldMap.get(rule.target);
      const active = target && !target.hide && !target.disable && (!rule.variantId || rule.variantId === variantId);
      const sources = rule.sources.map(key => {
        const field = this.product.fieldMap.get(key);
        if (!field || field.hide || field.disable) return null;
        return field.values.find(value => value.selected && !value.hide && !value.disable);
      });
      const complete = active && sources.every(Boolean);
      const total = complete ? sources.reduce((sum, value) => sum + this.lengthInFeet(value.label), 0) : NaN;
      const match = complete ? target.values.find(value =>
        !value.hide && !value.disable && this.lengthInFeet(value.label) === total
      ) : null;
      if (target && target.values.some(value => Boolean(value.selected) !== Boolean(match && value.key === match.key))) {
        this.product.updateOptionByKey(rule.target, match ? [{ key: match.key }] : []);
        changed = true;
      }
      // Hide the control only: the field must still be priced, validated and submitted.
      select.hidden = true;
      select.style.display = 'none';
      select.setAttribute('aria-hidden', 'true');
      select.input.setAttribute('tabindex', '-1');
      select.input.setAttribute('aria-expanded', 'false');
      select.input.removeAttribute('aria-activedescendant');
      select.optionsContainer.style.display = 'none';
      for (const option of select.options) {
        option.classList.toggle('tfs__option--selected', Boolean(match && option.dataset.key === match.key));
      }
      select.value = match?.label || '';
      result.textContent = match?.label || '';
      result.hidden = !match;
      if (complete && !match) {
        const message = 'Unable to match the total length. Please check your selected side lengths.';
        if (select.error?.textContent !== message) select.createError(message);
        this.invalidField = this.invalidField || fieldset;
      } else {
        select.clearError();
      }
    }
    if (changed) this.product.updateOptionShowOrHide();
  }
  destroy() {
    this.abortController.abort();
    for (const { select, result, hidden, display, ariaHidden, tabIndex } of this.controls) {
      result.remove();
      select.clearError();
      select.hidden = hidden;
      select.style.display = display;
      if (ariaHidden === null) select.removeAttribute('aria-hidden');
      else select.setAttribute('aria-hidden', ariaHidden);
      if (tabIndex === null) select.input.removeAttribute('tabindex');
      else select.input.setAttribute('tabindex', tabIndex);
    }
  }
}

class TpsProductParent extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.assembledFields = [];
    this.attrImagePreviews = {
      timer: null,
      currentNode: null,
      clickController: null,
      width: 220,
      rightWidth: 248,
      isHoverDevice: window.matchMedia('(hover: hover) and (pointer: fine)').matches
    }
    this._wrapper = this.closest('.tps-product-template');
    this.installationZipSet = null;
    this.installationSkuMapPromise = null;
    this.boundAddToCartForm = null;
    this.boundInstallationService = null;
    this.boundQuantityInput = null;
    this.installationState = {
      selected: false,
      isAvailable: false,
      zip: '',
      serviceType: '',
      serviceTag: '',
      serviceSkuMapUrl: '',
      serviceSkuMap: null,
      draperyMaxHeight: 120,
      draperyTierMaxWidth: 225,
      hasConflict: false
    };
    this.handlePageShowSync = this.handlePageShowSync.bind(this);
    this.handleVisibilitySync = this.handleVisibilitySync.bind(this);
  }
  get defaultOptions() {
    const textContent = this._wrapper.querySelector(`script[type="application/json"][data-selected-variant]`)?.textContent;
    return textContent ? JSON.parse(textContent) : {};
  }
  get skeleton() {
    return this._wrapper.querySelector('.tps-field-skeleton');
  }
  get skuImage() {
    return this._wrapper.querySelectorAll('.image-swatch[data-preview-image]');
  }
  get selectField() {
    return this.querySelector('.tps-select-field');
  }
  get quantityInput() {
    return this.querySelector('tps-quantity-input');
  }
  get addToCartForm() {
    return this._wrapper.querySelector('.add-to-cart-form');
  }
  get addToCartButton() {
    return this.addToCartForm?.querySelector('button.tps-add-to-cart');
  }
  get slides() {
    return this._wrapper.querySelector('tps-main-template-slides')
  }
  get selectedKeys() {
    return this.assembledFields.filter(item => !item.hide).reduce((pre, cur) => {
      return pre.concat(cur.values?.filter((value) => value.selected))
    }, []).filter((item) => item?.key);
  }
  get addToCartFormRect() {
    return this.addToCartForm.getBoundingClientRect();
  }
  normalizeZip(zip = '') {
    return `${zip}`.trim().toUpperCase().replace(/[\s-]/g, '');
  }
  normalizeServiceTag(tag = '') {
    return `${tag}`.trim().toUpperCase();
  }
  parseVariantId(value) {
    const variantId = parseInt(value, 10);
    return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
  }
  formatCartDateValue(value) {
    const rawValue = `${value || ''}`.trim();
    const parts = rawValue.split('/');

    if (parts.length !== 3) return rawValue;

    const [month, day, year] = parts;

    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  parseNumber(value, fallback = null) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  sanitizeSvcMetaValue(value = '') {
    return `${value || ''}`.replace(/[|]/g, '').trim();
  }
  buildSvcMeta({ kind = '', seed = '', serviceType = '', variantId = '', unitPrice = '' } = {}) {
    const pairs = ['v=1'];
    const safeKind = this.sanitizeSvcMetaValue(kind);
    const safeSeed = this.sanitizeSvcMetaValue(seed);
    const safeServiceType = this.sanitizeSvcMetaValue(serviceType);
    const safeVariantId = this.sanitizeSvcMetaValue(variantId);
    const safeUnitPrice = this.sanitizeSvcMetaValue(unitPrice);
    if (safeKind) pairs.push(`k=${safeKind}`);
    if (safeSeed) pairs.push(`seed=${safeSeed}`);
    if (safeServiceType) pairs.push(`t=${safeServiceType}`);
    if (safeVariantId) pairs.push(`vid=${safeVariantId}`);
    if (safeUnitPrice) pairs.push(`up=${safeUnitPrice}`);
    return pairs.join('|');
  }
  getMainProductTitle() {
    const desktopTitle = this._wrapper?.querySelector('.tps-product-template__header_pc h1')?.textContent?.trim();
    if (desktopTitle) return desktopTitle;
    const mobileTitle = this._wrapper?.querySelector('.tps-product-template__header__h5 h2')?.textContent?.trim();
    if (mobileTitle) return mobileTitle;
    const documentTitle = document?.title ? document.title.split('|')[0].trim() : '';
    return documentTitle || 'Product';
  }
  getCurrentMainQuantity() {
    const quantityFromForm = parseInt(this.addToCartForm?.querySelector('input[name="quantity"]')?.value, 10);
    if (Number.isFinite(quantityFromForm) && quantityFromForm > 0) {
      return quantityFromForm;
    }
    const quantityFromComponent = parseInt(this.quantityInput?.value, 10);
    if (Number.isFinite(quantityFromComponent) && quantityFromComponent > 0) {
      return quantityFromComponent;
    }
    return 1;
  }
  getInstallationUnits(quantityNum, serviceType = '') {
    const normalizedQuantity = Math.max(parseInt(quantityNum, 10) || 1, 1);
    const normalizedServiceType = `${serviceType || ''}`.trim().toLowerCase();
    if (normalizedServiceType === 'drapery') {
      return Math.max(Math.ceil(normalizedQuantity / 2), 1);
    }
    return normalizedQuantity;
  }
  getCurrentDraperyWidthInches() {
    const sizeFields = this.assembledFields.filter((field) => field.optionType === 'size' && !field.hide);
    if (!sizeFields.length) return 0;
    return sizeFields.reduce((maxWidth, field) => {
      const width = parseFloat(field.tps_width_inches || field.tps_width_value);
      if (!Number.isFinite(width)) return maxWidth;
      return Math.max(maxWidth, width);
    }, 0);
  }
  normalizeInstallationSeedPart(value, fallback = 'no-width') {
    const normalized = `${value || ''}`
      .toLowerCase()
      .trim()
      .replace(/["']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return normalized || fallback;
  }
  buildInstallationParentKeySeed(mainVariantId) {
    const parentProductId = this._wrapper?.dataset?.pid || `${mainVariantId || 'product'}`;
    const widthValue = this.getCurrentDraperyWidthInches();
    const normalizedWidth = this.normalizeInstallationSeedPart(
      Number.isFinite(widthValue) && widthValue > 0 ? `${widthValue}` : 'no-width',
      'no-width'
    );
    const heightValue = this.getCurrentDraperyHeightInches();
    const normalizedHeight = this.normalizeInstallationSeedPart(
      Number.isFinite(heightValue) && heightValue > 0 ? `${heightValue}` : 'no-height',
      'no-height'
    );
    return `${parentProductId}-${normalizedWidth}-${normalizedHeight}`;
  }
  getCurrentDraperyHeightInches() {
    const sizeFields = this.assembledFields.filter((field) => field.optionType === 'size' && !field.hide);
    if (!sizeFields.length) return 0;
    return sizeFields.reduce((maxHeight, field) => {
      const height = parseFloat(field.tps_length_inches || field.tps_length_value);
      if (!Number.isFinite(height)) return maxHeight;
      return Math.max(maxHeight, height);
    }, 0);
  }
  getInstallationZipSet() {
    if (this.installationZipSet) return this.installationZipSet;
    const zipList = Array.isArray(window.TPS_INSTALL_ZIP_CODES) && window.TPS_INSTALL_ZIP_CODES.length
      ? window.TPS_INSTALL_ZIP_CODES
      : (theme?.tps?.addZipCode || '').split(',');
    this.installationZipSet = new Set(zipList.map((item) => this.normalizeZip(item)).filter(Boolean));
    return this.installationZipSet;
  }
  async loadInstallationSkuMap() {
    if (this.installationState.serviceSkuMap) {
      return this.installationState.serviceSkuMap;
    }
    if (this.installationSkuMapPromise) {
      return this.installationSkuMapPromise;
    }
    const url = this.installationState.serviceSkuMapUrl;
    if (!url) return null;
    this.installationSkuMapPromise = fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load installation sku map: ${response.status}`);
        }
        return response.json();
      })
      .then((map) => {
        this.installationState.serviceSkuMap = map;
        return map;
      })
      .catch((error) => {
        console.error('[tps-installation-service] Failed to load sku map', error);
        this.installationState.serviceSkuMap = null;
        return null;
      });
    return this.installationSkuMapPromise;
  }
  getInstallationServiceConfig() {
    const map = this.installationState.serviceSkuMap;
    if (!map || typeof map !== 'object') return null;
    const { tags = {} } = map;
    if (!tags || typeof tags !== 'object') return null;
    const serviceTag = this.normalizeServiceTag(this.installationState.serviceTag);
    if (!serviceTag) return null;
    const config = tags[serviceTag];
    if (!config || typeof config !== 'object') return null;
    return config;
  }
  getInstallationDetailsPayload(config) {
    const detailsModal = config?.detailsModal;
    if (!detailsModal || typeof detailsModal !== 'object') return null;
    const title = `${detailsModal.title || ''}`.trim() || 'In-home Installation';
    const sourceSections = Array.isArray(detailsModal.sections) ? detailsModal.sections : [];
    const sections = sourceSections.map((section) => {
      const normalizedSection = {
        title: `${section?.title || ''}`.trim(),
        bullets: Array.isArray(section?.bullets)
          ? section.bullets.map((item) => `${item || ''}`.trim()).filter(Boolean)
          : [],
        paragraphs: Array.isArray(section?.paragraphs)
          ? section.paragraphs.map((item) => `${item || ''}`.trim()).filter(Boolean)
          : [],
        faq: Array.isArray(section?.faq)
          ? section.faq
            .map((item) => ({
              question: `${item?.question || ''}`.trim(),
              answer: `${item?.answer || ''}`.trim()
            }))
            .filter((item) => item.question || item.answer)
          : []
      };
      return normalizedSection;
    }).filter((section) => section.title || section.bullets.length || section.paragraphs.length || section.faq.length);
    if (!sections.length) return null;
    return {
      title,
      sections
    };
  }
  refreshInstallationDetailsButtonState() {
    const installationService = this._wrapper.querySelector('[data-installation-service]');
    if (!installationService) return;
    const detailsButton = installationService.querySelector('[data-installation-see-details]');
    if (!detailsButton) return;
    const serviceConfig = this.getInstallationServiceConfig();
    const detailsPayload = this.getInstallationDetailsPayload(serviceConfig);
    const hasDetails = Boolean(detailsPayload);
    detailsButton.disabled = !hasDetails;
    detailsButton.hidden = !hasDetails;
  }
  getSizeLimitFromTitle(title = '') {
    const matched = `${title}`.trim().match(/(\d+(?:\.\d+)?)$/);
    if (!matched) return null;
    return this.parseNumber(matched[1], null);
  }
  getTitleVariantsFromConfig(config) {
    const variants = Array.isArray(config?.variants) ? config.variants : [];
    return variants
      .map((variant) => ({
        ...variant,
        title: `${variant?.title || ''}`.trim(),
        variantId: this.parseVariantId(variant?.variantId),
        price: this.parseNumber(variant?.price),
        sizeLimitIn: this.getSizeLimitFromTitle(variant?.title)
      }))
      .filter((variant) => variant.title && Number.isFinite(variant.price));
  }
  resolveInstallationVariantBySize(config, widthInches, serviceType = '') {
    const variants = this.getTitleVariantsFromConfig(config);
    if (!variants.length) return null;
    const variantsWithLimit = variants
      .filter((variant) => Number.isFinite(variant.sizeLimitIn))
      .sort((a, b) => a.sizeLimitIn - b.sizeLimitIn);
    if (variantsWithLimit.length) {
      this.installationState.draperyTierMaxWidth = variantsWithLimit[variantsWithLimit.length - 1].sizeLimitIn;
    }
    const normalizedWidth = this.parseNumber(widthInches);
    if (serviceType === 'drapery' && (!Number.isFinite(normalizedWidth) || normalizedWidth <= 0)) {
      return null;
    }
    if (serviceType === 'blinds' && variants.length === 1) {
      return variants[0];
    }
    if (Number.isFinite(normalizedWidth) && normalizedWidth > 0 && variantsWithLimit.length) {
      return variantsWithLimit.find((variant) => normalizedWidth <= variant.sizeLimitIn) || null;
    }
    return variants[0];
  }
  setInstallationStatus(status = 'idle', reason = 'default') {
    const installationService = this._wrapper.querySelector('[data-installation-service]');
    if (!installationService) return;
    const availableText = installationService.querySelector('[data-installation-available]');
    const unavailableText = installationService.querySelector('[data-installation-unavailable]');
    if (!availableText || !unavailableText) return;
    availableText.hidden = status !== 'available';
    unavailableText.hidden = status !== 'unavailable';
    if (status === 'unavailable') {
      const defaultText = unavailableText.dataset.defaultText || 'Not available';
      const overHeightText = unavailableText.dataset.overHeightText || defaultText;
      const overWidthText = unavailableText.dataset.overWidthText || defaultText;
      if (reason === 'overHeight') {
        unavailableText.textContent = overHeightText;
      } else if (reason === 'overWidth') {
        unavailableText.textContent = Number.isFinite(this.installationState.draperyTierMaxWidth)
          ? overWidthText
          : defaultText;
      } else {
        unavailableText.textContent = defaultText;
      }
    }
  }
  refreshInstallationMatchedPrice() {
    const installationService = this._wrapper.querySelector('[data-installation-service]');
    if (!installationService) return;
    const priceEl = installationService.querySelector('[data-installation-matched-price]');
    const startingFromEl = installationService.querySelector('[data-installation-starting-from]');
    if (!priceEl) return;
    this.loadInstallationSkuMap().then(() => {
      const serviceConfig = this.getInstallationServiceConfig();
      const widthInches = this.getCurrentDraperyWidthInches();
      const matchedVariant = this.resolveInstallationVariantBySize(
        serviceConfig,
        widthInches,
        this.installationState.serviceType
      );
      if (!matchedVariant || !Number.isFinite(matchedVariant.price)) {
        priceEl.hidden = true;
        if (startingFromEl) startingFromEl.hidden = false;
        return;
      }
      const quantityNum = this.getCurrentMainQuantity();
      const units = this.getInstallationUnits(quantityNum, this.installationState.serviceType);
      const totalAmount = matchedVariant.price * units;
      priceEl.textContent = `Service price: $${totalAmount.toFixed(2)}`;
      priceEl.hidden = false;
      if (startingFromEl) startingFromEl.hidden = true;
    });
  }
  syncInstallationStateFromInputs({ updateUI = false } = {}) {
    const installationService = this._wrapper.querySelector('[data-installation-service]');
    if (!installationService || this.installationState.hasConflict) return;
    const checkbox = installationService.querySelector('[data-installation-enable]');
    const zipInput = installationService.querySelector('[data-installation-zip]');
    if (!checkbox || !zipInput) return;
    const zip = `${zipInput.value || ''}`.trim();
    const minZipLength = 3;
    this.installationState.zip = zip;
    if (!zip || zip.length < minZipLength) {
      this.installationState.isAvailable = false;
      checkbox.disabled = true;
      if (checkbox.checked) checkbox.checked = false;
      this.installationState.selected = false;
      if (updateUI) {
        this.setInstallationStatus('idle');
      }
      return;
    }
    let isFormatValid = true;
    if (typeof zipInput.checkValidity === 'function') {
      try {
        isFormatValid = zipInput.checkValidity();
      } catch (error) {
        console.error('[tps-installation-service] ZIP pattern validation failed', error);
        isFormatValid = false;
      }
    }
    const available = isFormatValid && this.getInstallationZipSet().has(this.normalizeZip(zip));
    this.installationState.isAvailable = available;
    checkbox.disabled = !available;
    if (!available && checkbox.checked) checkbox.checked = false;
    this.installationState.selected = available ? Boolean(checkbox.checked) : false;
    if (updateUI) {
      this.setInstallationStatus(available ? 'available' : 'unavailable', 'default');
    }
  }
  initInstallationService() {
    const installationService = this._wrapper.querySelector('[data-installation-service]');
    if (!installationService) return;
    const hasConflict = installationService.dataset.installationTagConflict === 'true';
    this.installationState.hasConflict = hasConflict;
    if (hasConflict) {
      console.error('[tps-installation-service] Product has both INSTALL-DRAPERY and INSTALL-BLINDS tags');
      return;
    }
    this.installationState.serviceType = installationService.dataset.serviceType || '';
    this.installationState.serviceTag = this.normalizeServiceTag(installationService.dataset.serviceTag || '');
    this.installationState.serviceSkuMapUrl = installationService.dataset.installationSkuMapUrl || '';
    this.installationState.draperyMaxHeight = parseFloat(installationService.dataset.draperyMaxHeight) || 120;
    this.loadInstallationSkuMap().then(() => {
      this.refreshInstallationMatchedPrice();
      this.refreshInstallationDetailsButtonState();
    });
    if (this.boundQuantityInput !== this.quantityInput) {
      this.boundQuantityInput = this.quantityInput;
      if (this.boundQuantityInput) {
        const qtyNativeInput = this.boundQuantityInput.querySelector('input[name="quantity"]');
        qtyNativeInput?.addEventListener('input', () => {
          this.refreshInstallationMatchedPrice();
        });
        qtyNativeInput?.addEventListener('change', () => {
          this.refreshInstallationMatchedPrice();
        });
        this.boundQuantityInput.addEventListener('click', (event) => {
          if (event.target.closest('.tps-quantity__icon')) {
            requestAnimationFrame(() => {
              this.refreshInstallationMatchedPrice();
            });
          }
        });
      }
    }
    if (this.boundInstallationService === installationService) {
      this.syncInstallationStateFromInputs({ updateUI: true });
      this.refreshInstallationDetailsButtonState();
      return;
    }
    this.boundInstallationService = installationService;
    const checkbox = installationService.querySelector('[data-installation-enable]');
    const zipInput = installationService.querySelector('[data-installation-zip]');
    const detailsButton = installationService.querySelector('[data-installation-see-details]');
    if (!checkbox || !zipInput) return;
    this.refreshInstallationDetailsButtonState();
    if (detailsButton) {
      detailsButton.addEventListener('click', async (event) => {
        event.preventDefault();
        await this.loadInstallationSkuMap();
        const serviceConfig = this.getInstallationServiceConfig();
        const detailsPayload = this.getInstallationDetailsPayload(serviceConfig);
        if (!detailsPayload) return;
        const sidePopup = TpsSidePopupModal.getSharedInstance();
        sidePopup.open(detailsPayload, detailsButton);
      });
    }
    this.syncInstallationStateFromInputs({ updateUI: true });
    checkbox.addEventListener('change', () => {
      this.syncInstallationStateFromInputs({ updateUI: true });
    });
    zipInput.addEventListener('input', () => {
      this.syncInstallationStateFromInputs({ updateUI: true });
    });
  }
  async getInstallationServiceItem(mainVariantId, mainQuantity, parentKeySeed) {
    const installationService = this._wrapper.querySelector('[data-installation-service]');
    if (!installationService || this.installationState.hasConflict) {
      return { ok: true, item: null };
    }
    this.syncInstallationStateFromInputs({ updateUI: true });
    if (!this.installationState.selected) {
      return { ok: true, item: null };
    }
    if (!this.installationState.zip) {
      this.setInstallationStatus('unavailable', 'default');
      return { ok: false, item: null };
    }
    if (!this.installationState.isAvailable) {
      this.setInstallationStatus('unavailable', 'default');
      return { ok: false, item: null };
    }
    if (!['drapery', 'blinds'].includes(this.installationState.serviceType)) {
      console.error('[tps-installation-service] Invalid service type');
      return { ok: false, item: null };
    }
    await this.loadInstallationSkuMap();
    const serviceConfig = this.getInstallationServiceConfig();
    if (!serviceConfig) {
      console.error('[tps-installation-service] Missing service config in sku map');
      return { ok: false, item: null };
    }
    const quantityNum = Math.max(parseInt(mainQuantity, 10) || 1, 1);
    const widthInches = this.getCurrentDraperyWidthInches();
    const matchedVariant = this.resolveInstallationVariantBySize(
      serviceConfig,
      widthInches,
      this.installationState.serviceType
    );
    if (!matchedVariant) {
      this.setInstallationStatus('unavailable', 'overWidth');
      return { ok: false, item: null };
    }
    const serviceVariantId = matchedVariant.variantId;
    const finalUnitPrice = matchedVariant.price;
    const stableParentKeySeed = parentKeySeed || this.buildInstallationParentKeySeed(mainVariantId);
    const mainProductTitle = this.defaultOptions?.name || this.getMainProductTitle();
    if (this.installationState.serviceType === 'drapery') {
      const configMaxHeight = this.parseNumber(serviceConfig?.maxHeightIn);
      const draperyMaxHeight = Number.isFinite(configMaxHeight) ? configMaxHeight : this.installationState.draperyMaxHeight;
      const currentHeight = this.getCurrentDraperyHeightInches();
      if (currentHeight > draperyMaxHeight) {
        this.setInstallationStatus('unavailable', 'overHeight');
        return { ok: false, item: null };
      }
    }
    if (!serviceVariantId) {
      console.error('[tps-installation-service] Missing matched service variant id in sku map');
      return { ok: false, item: null };
    }
    if (!Number.isFinite(finalUnitPrice) || finalUnitPrice <= 0) {
      console.error('[tps-installation-service] Invalid matched service price');
      return { ok: false, item: null };
    }
    const units = this.getInstallationUnits(quantityNum, this.installationState.serviceType);
    const item = {
      id: serviceVariantId,
      quantity: units,
      unitPrice: finalUnitPrice,
      properties: {
        _svc_zip: this.installationState.zip,
        'Service For': mainProductTitle,
        _svc_meta: this.buildSvcMeta({
          kind: 's',
          seed: stableParentKeySeed,
          serviceType: this.installationState.serviceType
        })
      }
    };
    return { ok: true, item };
  }
  encrypt(num, times = 100) {
    const base = Math.round(num * times) + 622152;
    return base * 10 + 7;
  }
  handleize(name) {
    if (!name || typeof name !== 'string') {
      return '';
    }
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }
  priceFromCents(cents) {
    const str = String(Math.round(cents)).padStart(3, '0');
    return str.slice(0, -2) + '.' + str.slice(-2);
  }
  checkEdgePrice(list, value) {
    if (list.type === 'range') {
      return value >= list.min && value <= list.max;
    }
    if (list.type === 'exact') {
      return value === list.value;
    }
    return false;
  }
  computedPrice(priceRule = {}) {
    if (priceRule.ruleType === 'fixed') {
      return priceRule.amount * 100;
    } else if (priceRule.ruleType === 'percentage') {
      return this.defaultOptions.price * priceRule.rate;
    }
    return 0;
  }
  computedEdgePrice(lists, type, value) {
    const list = lists.find((list) => list.edgeType === type && this.checkEdgePrice(list, value));
    if (!list) return 0;
    if (list.type == 'range') {
      const { min, stepSize, stepStart, stepPrice, price } = list;
      let totalPrice = 0;
      const isValidStepStart = Number.isFinite(stepStart) && stepStart >= 0;
      if (isValidStepStart) {
        totalPrice = stepStart * 100;
        let step = Math.ceil((value - min) / stepSize);
        totalPrice += step * stepPrice * 100;
      } else {
        totalPrice = price * 100;
      }
      return totalPrice;
    }
    if (list.type == 'exact') {
      return list.price * 100;
    }
    return 0;
  }
  computedSizePrice(option = {}) {
      const { pricingRule = {}, tps_width_inches, tps_length_inches } = option;
      if (!tps_width_inches || !tps_length_inches) return 0;
      const { dimension, tiers = [] } = pricingRule;
      const width = tps_width_inches * 1;
      const length = tps_length_inches * 1;
      const area = width * length;
      if (dimension === "edge") {
        const fieldWidthPrice = this.computedEdgePrice(tiers, 'width', width);
        const fieldLengthPrice = this.computedEdgePrice(tiers, 'length', length);
        return fieldWidthPrice + fieldLengthPrice;
      }
      if (dimension === "area") {
        return this.computedEdgePrice(tiers, '', area);
      }
      return 0;
  }
  createSizeTitle(option) {
    const { tps_width_value, tps_length_value, tps_width_fraction, tps_length_fraction, tps_size_unit } = option;
    if (!tps_width_value || !tps_length_value) return '';
    let width_fraction = '';
    let length_fraction = '';
    if (tps_width_fraction && !['-/-','0/0'].includes(tps_width_fraction)) {
      width_fraction = ` ${tps_width_fraction}`;
    }
    if (tps_length_fraction && !['-/-','0/0'].includes(tps_length_fraction)) {
      length_fraction = ` ${tps_length_fraction}`;
    }
    let width = `${tps_width_value}${width_fraction}"W`;
    let length = `${tps_length_value}${length_fraction}"L`;
    if (tps_size_unit === 'cm') {
      width = `${tps_width_value}cm W`;
      length = `${tps_length_value}cm L`;
    }
    return `${width} x ${length}`;
  }
  computedNumberPrice(option = {}) {
      const { pricingRule = {}, current_value } = option;
      if (!current_value) return 0;
      const { tiers = [] } = pricingRule;
      const value = current_value * 1;
      return this.computedEdgePrice(tiers, '', value);
  }
  escapeHtml(value = '') {
    return `${value || ''}`
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  bindClickClose() {
    if ('AbortController' in window) {
      this.attrImagePreviews.clickController?.abort();
      this.attrImagePreviews.clickController = new AbortController();
      document.addEventListener('click', this.onClickOutside.bind(this), { signal: this.attrImagePreviews.clickController.signal });
    } else {
      document.addEventListener('click', this.onClickOutside.bind(this));
    }
  }
  onClickOutside(e) {
    const wrapper = this.attrImagePreviews.currentNode.closest('.image-swatch-wrapper');
    if (e.target.closest('.attr-image-preview') || wrapper.contains(e.target)) {
      return;
    }
    this.removePreviewImage();
    this.unbindClickOutside();
  }
  unbindClickOutside() {
    if (this.attrImagePreviews.clickController) {
      this.attrImagePreviews.clickController.abort();
      this.attrImagePreviews.clickController = null;
    } else {
      document.removeEventListener('click', this.onClickOutside);
    }
  }
  removePreviewImage() {
    const previewNode = document.querySelector('.attr-image-preview');
    previewNode?.remove();
    clearTimeout(this.attrImagePreviews.timer);
  }
  createPreviewImage(image) {
    let { width, rightWidth } = this.attrImagePreviews;
    const fieldType = image.closest('fieldset')?.dataset.type || '';
    let imageContent = '';
    let colorTitle = '';
    if (fieldType === 'Color') {
      colorTitle = this.escapeHtml(image.dataset.title);
      let colorSkuFields = [];
      try {
        const fields = JSON.parse(image.dataset.colorSkuField || '[]');
        colorSkuFields = Array.isArray(fields) ? fields : [];
      } catch (error) {
        colorSkuFields = [];
      }
      const fieldItems = colorSkuFields.map((item) => {
        const field = `${item || ''}`.split('||');
        if (field.length !== 2) return '';
        return `<li><span>${this.escapeHtml(field[0].trim())}: </span><span>${this.escapeHtml(field[1].trim())}</span></li>`;
      }).join('');
      if (fieldItems) {
        imageContent = `<h3>${colorTitle}</h3><ul>${fieldItems}</ul>`;
      }
    }
    const fallbackContent = fieldType === 'Color' && !imageContent ? colorTitle : '';
    if (imageContent && width + rightWidth > window.innerWidth) {
      rightWidth = (window.innerWidth - 32) / 2;
      width = rightWidth;
    }

    const previewWidth = imageContent ? width + rightWidth : width;
    const { left, top, bottom, width: imageWidth } = image.getBoundingClientRect();
    const innerWidth = window.innerWidth;
    const widthCenter = left + imageWidth / 2;
    const safeGap = 8;
    const rightSpace = widthCenter + previewWidth / 2 <= innerWidth - safeGap;
    const leftSpace = widthCenter - previewWidth / 2 >= safeGap;
    const topSpace = top - width > 88;
    let topPosition = topSpace ? top - width - 22 : bottom + 16;
    if (fallbackContent) {
      topPosition = topSpace ? topPosition - 16 : topPosition - 8;
    }
    let leftPosition;
    if (rightSpace && leftSpace) {
      leftPosition = widthCenter - previewWidth / 2;
    } else if (!rightSpace) {
      leftPosition = innerWidth - previewWidth - safeGap;
    } else {
      leftPosition = safeGap;
    }

    const arrowGap = 16;
    const arrowLeft = Math.min(Math.max(widthCenter - leftPosition, arrowGap), previewWidth - arrowGap);
    this.removePreviewImage();
    const previewClass = imageContent ? 'attr-image-preview--Color' : fallbackContent ? 'attr-image-preview--color' : '';
    const node = `<div style="--width: ${width}px;--field-width: ${rightWidth}px;--preview-width: ${previewWidth}px;--left: ${leftPosition}px;--top: ${topPosition}px;--arrow-left: ${arrowLeft}px;" class="attr-image-preview ${previewClass} ${topSpace ? 'attr-image-preview--top' : 'attr-image-preview--bottom'}">
      <img width="100%" height="100%" src="${this.escapeHtml(image.dataset.previewImage)}">
      ${imageContent ? `<div class="attr-image-preview__content">${imageContent}</div>` : fallbackContent ? `<div class="attr-image-preview__content">${fallbackContent}</div>` : ''}
    </div>`;
    document.body.insertAdjacentHTML('beforeend', node);
    if (!this.attrImagePreviews.isHoverDevice) {
      this.attrImagePreviews.currentNode = image;
      window.addEventListener('scroll', () => {
        this.removePreviewImage();
      }, { passive: true, once: true });
      this.bindClickClose();
      clearTimeout(this.attrImagePreviews.timer);
      this.attrImagePreviews.timer = setTimeout(() => {
        this.removePreviewImage();
      }, 5000)
    }
  }
  handlePreviewImage(image) {
    const input = image.closest('.image-swatch-wrapper')?.querySelector('input');
    if (!input) return;
    if (this.attrImagePreviews.isHoverDevice) {
      let timer = null;
      input.addEventListener('mouseenter', () => {
        timer = setTimeout(() => {
          this.createPreviewImage(image);
        }, 200);
      });
      input.addEventListener('mouseleave', () => {
        this.removePreviewImage();
        clearTimeout(timer);
      });
    } else {
      input.addEventListener('click', () => {
        this.createPreviewImage(image);
      })
    }
  }
  attrImagePreview() {
    if (!this.skuImage.length) return;
    for (const image of this.skuImage) {
      const previewImage = image.dataset.previewImage;
      if (!previewImage) continue;
      this.handlePreviewImage(image)
    }
  }
  updateExpandButton() {
    if (!this.showExpandButton) return;
    this.showExpandButton.classList.toggle('color-show', Boolean(this.showExpandButtonStatus));
  }
  expandButtonStatusHandle() {
    if (!this.showExpandButton) return;
    if (this.expandButtonStatus) {
      this.colorFieldImages.classList.add('expanded');
    } else {
      this.colorFieldImages.classList.remove('expanded');
    }
    this.showExpandButton.innerHTML = this.expandButtonStatus ? 'Collapse Colors' : 'Expand Colors';
  }
  colorImageToggle() {
    this.colorFields = this._wrapper.querySelector('fieldset[data-type="Color"]');
    this.colorFieldImages = this.colorFields?.querySelector('tps-field-color-image');
    this.showExpandButton = this.colorFields?.querySelector('.tps-field-color-toggle');
    if (!this.colorFieldImages) return;
    if (window.innerWidth > 768) return;
    const scrollWidth = this.colorFieldImages.scrollWidth;
    const clientWidth = this.colorFieldImages.clientWidth;
    this.expandButtonStatus = false;
    this.showExpandButton?.addEventListener('click', () => {
      this.expandButtonStatus = !this.expandButtonStatus;
      this.expandButtonStatusHandle();
    });
    if (scrollWidth > clientWidth) {
      this.showExpandButtonStatus = true;
      this.updateExpandButton();
    }
  }
  initOptions() {
    const { options } = this.defaultOptions;
    const fields = this.fields.filter(item => !(
      item.source === 1 &&
      item.values?.length === 1 &&
      item.values[0]?.displayValue === 'Default Title'
    ));
    this.assembledFields = fields.map((field, _index) => {
      const { optionKey, optionType, source, validationRules, values } = field;
      const _values = (values || []).map((value) => {
        const { valueKey, displayValue, isDefault } = value
        const selected = source == 1 ? options.includes(displayValue) : isDefault;
        return {
          ...value,
          key: valueKey,
          label: displayValue,
          optionKey,
          source,
          selected
        }
      });
      if (['text', 'number', 'date'].includes(optionType)) {
        field.current_value = validationRules?.defaultValue;
      }
      return {
        ...field,
        key: optionKey,
        values: _values,
        fieldIndex: _index
      };
    });
    for (const field of this.assembledFields) {
      this.fieldMap.set(field.key, field);
      if (field.conditionalLogic) { this.ruleMap.set(field.key, field.conditionalLogic); }
      for (const value of field.values) {
        this.fieldMap.set(value.key, value);
        if (value.conditionalLogic) { this.ruleMap.set(value.key, value.conditionalLogic); }
      }
    }
  }
  customFieldsData() {
    this.optionLinkage?.destroy();
    const fieldScript = this._wrapper.querySelector('script[type="application/json"][data-custom-fields]');
    this.fields = fieldScript?.textContent ? JSON.parse(fieldScript.textContent) : [];
    fieldScript?.remove();
    this.assembledFields = [];
    this.fieldMap = new Map();
    this.ruleMap = new Map();
    this._totalPrice = 0;
    this.initOptions();
    this.optionLinkage = TpsProductOptionLinkage.create(this);
  }
  initWebComponents() {
    this.updateField();
    this.attrImagePreview();
    this.colorImageToggle();
    this.initInstallationService();
    this.handlePropertiesSubmit();
    this.bcpoDomRemove();
    this.updateAddToCartButtonState();
  }
  
  initFieldEvent() {
    this.addEventListener("update-field-input", (e) => {
      const key = e.target.closest('fieldset').dataset.key;
      const value = e.target.value;
      this.updateField(key, { current_value: value });
    });
    this.addEventListener("update-field-checked", (e) => {
      const key = e.target.closest('fieldset').dataset.key;
      const value = e.target.value;
      this.updateField(key, value);
    });
    this.addEventListener("update-field-size", (e) => {
      const {
        widthInputValue,
        lengthInputValue,
        widthFractionSelectValue,
        lengthFractionSelectValue,
        widthInchesValue,
        lengthInchesValue,
        sizeUnit
      } = e.target;
      const key = e.target.closest('fieldset').dataset.key;
      this.updateField(key, {
        tps_width_value: widthInputValue,
        tps_width_fraction: widthFractionSelectValue,
        tps_length_value: lengthInputValue,
        tps_length_fraction: lengthFractionSelectValue,
        tps_width_inches: widthInchesValue,
        tps_length_inches: lengthInchesValue,
        tps_size_unit: sizeUnit
      });
    });
  }
  connectedCallback() {
    this.initFieldEvent();
    this.observeAddToCart();
    window.addEventListener('pageshow', this.handlePageShowSync);
    document.addEventListener('visibilitychange', this.handleVisibilitySync);
  }
  disconnectedCallback() {
    this.optionLinkage?.destroy();
    window.removeEventListener('pageshow', this.handlePageShowSync);
    document.removeEventListener('visibilitychange', this.handleVisibilitySync);
  }
  syncInstallationStateAfterRestore() {
    const runSync = () => {
      this.syncInstallationStateFromInputs({ updateUI: true });
      this.refreshInstallationMatchedPrice();
    };
    requestAnimationFrame(() => {
      runSync();
      requestAnimationFrame(runSync);
    });
  }
  handlePageShowSync() {
    this.syncInstallationStateAfterRestore();
  }
  handleVisibilitySync() {
    if (document.visibilityState !== 'visible') return;
    this.syncInstallationStateAfterRestore();
  }
  changeLoadingShineState(state = 'add') {
    if (state === 'remove') {
      this.addToCartButton?.classList.remove('tps-loading-shine-btn');
      return;
    }
    this.addToCartButton?.classList.add('tps-loading-shine-btn');
  }
  updateAddToCartButtonState(initializing = false) {
    const button = this.addToCartButton;
    if (!button) return;
    if (initializing) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.changeLoadingShineState();
      return;
    }
    const canAddToCart = button.dataset.canAddToCart === 'true';
    const isComingSoon = button.dataset.isComingSoon === 'true';
    button.disabled = !(canAddToCart && !isComingSoon);
    button.setAttribute('aria-busy', 'false');
    this.changeLoadingShineState('remove');
  }
  changeLoadingState(state = 'add') {
    if (state === 'remove') {
      this.addToCartButton?.classList.remove('tps-loading-btn');
      return;
    }
    this.addToCartButton?.classList.add('tps-loading-btn');
  }
  updateOptionValues(html) {
    const variantSelects = html.querySelector('tps-variant-picker');
    const variantSelectors = this._wrapper.querySelector('tps-variant-picker');
    if (variantSelects) {
      Morph.morph(variantSelectors, variantSelects);
      this.updateExpandButton();
      this.expandButtonStatusHandle();
    }
  }
  updateAddToCartButton(html) {
    const addToCart = html.querySelector('.product-form__buttons');
    const _addToCart = this._wrapper.querySelector('.product-form__buttons');
    if (addToCart) {
      Morph.morph(_addToCart, addToCart);
      this.updateAddToCartButtonState();
    }
  }
  updateColorImage(target) {
    const type = target.closest('fieldset').dataset.type;
    const imageSwitchOption = this.slides?.dataset.imageSwitchOption;
    if (!imageSwitchOption || type !== imageSwitchOption) return;
    const showMultipleImage = this.slides.dataset.showMultipleImage;
    if (showMultipleImage === 'multiple') {
      const color_name = this.handleize(target.value);
      const showSlide = this.slides.querySelector(`tps-main-template-slide[data-key="${color_name}"]`);
      if (!showSlide) return;
      const currentSlide = this.slides.querySelector('tps-main-template-slide.active');
      currentSlide.classList.remove('active');
      showSlide.classList.add('active');
    }
    if (showMultipleImage === 'single') {
      const featured_media = this.defaultOptions.featured_media;
      if (!featured_media) return;
      const nextSlide = this.slides.querySelector(`.product-template__image[data-key="${featured_media.id}"]`);
      if (!nextSlide) return;
      const index = nextSlide.dataset.index;
      const currentSlide = this.slides.querySelector('tps-main-template-slide.active');
      currentSlide.slideFlickity.select(index, true, true);
    }
  }
  updateOptionByKey(key, update) {
    const item = this.fieldMap.get(key);
    if (!item) return;
    const { optionType, values = [] } = item;
    if (['text', 'number', 'date', 'size'].includes(optionType)) {
      Object.assign(item, update);
      return;
    }
    const _values = values.map((value) => {
      value.selected = update.some((item) => item.key === value.key) ? true : false;
      return value;
    });
    Object.assign(item, { values: _values });
  }
  isSelected(key) {
    let dependItem = this.fieldMap.get(key);
    if (dependItem?.selected) {
      return !this.returnRuleChain(key);
    }
    return false;
  }
  returnRuleChain(key) {
    let item = this.fieldMap.get(key);
    if (!item) return false;
    if (!item.conditionalLogic) {
      if (key !== item.optionKey) {
        item = this.fieldMap.get(item.optionKey);
        if (!item) return false;
        if (!item.conditionalLogic) return false;
      } else { return false; }
    }
    const { conditions = [], relation } = item.conditionalLogic;
    const results = conditions.map((condition) => {
      const { comparator, valueKey } = condition;
      const isSelected = this.isSelected(valueKey);
      if (comparator === 'equals') {
        return isSelected;
      } else if (comparator === 'not_equals') {
        return !isSelected;
      }
      return false;
    });
    if (relation === 'and') {
      return results.every((result) => result);
    } else if (relation === 'or') {
      return results.some((result) => result);
    }
    return false;
  }
  updateOptionShowOrHide() {
    for (const option of this.assembledFields) {
      const { conditionalLogic, values = [], key } = option;
      for (const value of values) {
        if (!value.conditionalLogic) continue;
        const match = this.returnRuleChain(value.key);
        value[value.conditionalLogic.effect] = match;
      }
      if (!conditionalLogic) continue;
      const match = this.returnRuleChain(key);
      option[conditionalLogic.effect] = match;
    }
  }
  updateTotalPrice() {
    if (!this.defaultOptions.id) return;
    const initialPrice = this.defaultOptions.price * 1;
    let totalPrice = initialPrice;
    let compareAtPrice = this.defaultOptions.compare_at_price ? this.defaultOptions.compare_at_price * 1 : 0;
    for (const option of this.assembledFields) {
      if (option.source === 1) continue;
      if (option.hide) continue;
      if (option.optionType === 'size') {
        totalPrice += this.computedSizePrice(option);
        continue;
      }
      if (option.optionType === 'number' && option.pricingRule) {
        totalPrice += this.computedNumberPrice(option);
        continue;
      }
      for(const value of option.values) {
        if (value.hide) continue;
        if (value.disable) continue;
        if(value.selected) {
          totalPrice += this.computedPrice(value.pricingRule);
        }
      }
    }
    const priceElements = this._wrapper.querySelectorAll('.tps-product-template__price');
    for (const priceElement of priceElements) {
      const totalPriceHtml = theme.Currency.formatMoney(this.priceFromCents(totalPrice), theme.settings.moneyFormat);
      if (compareAtPrice > initialPrice) {
        const savePrice = compareAtPrice - initialPrice;
        const compareAtPriceHtml = theme.Currency.formatMoney(this.priceFromCents(totalPrice + savePrice), theme.settings.moneyFormat);
        const savePriceHtml = theme.Currency.formatMoney(this.priceFromCents(savePrice), theme.settings.moneyFormat);
        priceElement.innerHTML = `<span class="tps-product__sale">${totalPriceHtml}</span><span class="tps-product__compare">${compareAtPriceHtml}</span>`
      } else {
        priceElement.innerHTML = `<span class="tps-product__sale__on">${totalPriceHtml}</span>`;
      }
      this._totalPrice = totalPrice;
    }
  }
  updateTpsValue() {
     for (const option of this.assembledFields) {
      if (option.hide) {
        option.tps_value = '';
        continue;
      }
      if (option.optionType === 'size') {
        option.tps_value = this.createSizeTitle(option);
        continue;
      }
      if (option.optionType === 'date') {
        option.tps_value = option.current_value || '';
        continue;
      }
      if (option.optionType === 'text' && option.current_value) {
        option.tps_value = option.current_value;
        continue;
      }
      if (option.optionType === 'number' && option.current_value) {
        const displayUnit = option?.config?.displayUnit;
        option.tps_value = `${option.current_value}${displayUnit ? displayUnit : ''}`;
        continue;
      }
      option.tps_value = option.values
        .filter(value => value.selected && !value.hide && !value.disable)
        .map(value => value.label)
        .join(', ');
    }
  }
  updateCustomOption(option) {
    const field = this._wrapper.querySelector(`[data-key="${option.key}"]`);
    if (!field) return;
    field.setAttribute('aria-hidden', (option.hide || false));
    field.setAttribute('aria-disabled', (option.disable || false));
    for (const _value of (option.values || [])) {
      this.updateCustomOption(_value);
    }
  }
  updateCustomOptionTitle(option) {
    const { tps_value, key } = option;
    const field_title = this._wrapper.querySelector(`fieldset[data-key="${key}"] legend span.legend-value`);
    if (!field_title) return;
    if (field_title.innerHTML === tps_value) return;
    field_title.innerHTML = tps_value || '';
  }
  updateCustomOptionSort(option) {
    const { key, sortIndex } = option;
    const field_sort = this._wrapper.querySelector(`fieldset[data-key="${key}"] legend span.legend-sort`);
    if (!field_sort) return;
    const index = sortIndex + 1;
    if (field_sort.innerHTML === index) return;
    field_sort.innerHTML = index;
  }
  updateCustomOptionElement() {
    let offset = 0;
    for (const option of this.assembledFields) {
      if (option.hide) { offset = offset - 1; }
      option.sortIndex = option.fieldIndex + offset;
      if (option.source === 1) continue;
      this.updateCustomOption(option);
      this.updateCustomOptionTitle(option);
      this.updateCustomOptionSort(option);
    }
  }
  updateSelectFields() {
    if(!this.selectField) return;
    let selectHtml = '';
    for (const option of this.assembledFields) {
      selectHtml = selectHtml + (option.tps_value ? `<li class="tps-select-field_item">
        <span>${option.label}</span>
        <span>${option.tps_value}</span>
      </li>` : '');
    }
    this.selectField.innerHTML = selectHtml;
  }
  updateField(key, update) {
    this.updateOptionByKey(key, update);
    this.updateOptionShowOrHide();
    this.optionLinkage?.sync();
    this.updateTotalPrice();
    this.updateTpsValue();
    this.updateCustomOptionElement();
    this.updateSelectFields();
    this.refreshInstallationMatchedPrice();
  }
  renderProductInfo({ requestUrl, target }) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.updateAddToCartButtonState(true);
    return fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateOptionValues(html);
        this.updateAddToCartButton(html);
        this.updateField(target.dataset.optionKey, [{
          key: target.dataset.key
        }]);
        this.updateColorImage(target);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }
  validateFieldRule() {
    const validates = this.assembledFields.map((option) => {
      if (option.source === 1) return true;
      if (option.optionType === 'html') return true;
      if (option.hide) return true;
      const { optionType, key } = option;
      const fieldset = this._wrapper.querySelector(`fieldset[data-key="${key}"]`);
      if (optionType === 'text') {
        const fieldText = fieldset.querySelector('tps-field-text');
        const result = fieldText.validateRule();
        return result.ok; 
      }
      if (optionType === 'date') {
        const fieldDate = fieldset.querySelector('tps-field-date');
        const result = fieldDate.validateRule();
        return result.ok; 
      }
      if (optionType === 'number') {
        const fieldNumber = fieldset.querySelector('tps-field-number');
        const result = fieldNumber.validateRule();
        return result.ok;
      }
      if (optionType === 'size') {
        const fieldSize = fieldset.querySelector('tps-field-size');
        let  resultWidth = { ok: true };
        let  resultLength = { ok: true };
        let  resultFractionWidth = { ok: true };
        let  resultFractionLength = { ok: true };
        resultWidth = fieldSize.validateRule('width');
        resultLength = fieldSize.validateRule('length');
        resultFractionWidth = fieldSize.fractionValidateRule('width');
        resultFractionLength = fieldSize.fractionValidateRule('length');
        return resultWidth.ok && resultLength.ok && resultFractionWidth.ok && resultFractionLength.ok;
      }
      if (optionType === 'dropdown') {
        const fieldSelect = fieldset.querySelector('tps-field-select');
        const result = fieldSelect.validateRule();
        return result.ok;
      }
      if (optionType === 'image_swatch') {
        const fieldImage = fieldset.querySelector('tps-field-image');
        const result = fieldImage.validateRule();
        return result.ok;
      }
      if (optionType === 'button') {
        const fieldButton = fieldset.querySelector('tps-field-button');
        const result = fieldButton.validateRule();
        return result.ok;
      }
      if (optionType === 'radio' && option?.config?.radioType == 'normal') {
        const fieldRadio = fieldset.querySelector('tps-field-radio');
        const result = fieldRadio.validateRule();
        return result.ok;
      }
      if (optionType === 'file_upload') {
        const fieldFile = fieldset.querySelector('tps-field-upload');
        const result = fieldFile.validateRule();
        return result.ok;
      }
      return true;
    })
    return validates.every((validate) => validate);
  }
  focusFirstInvalidField() {
    const invalidControl = [...this._wrapper.querySelectorAll('[aria-invalid="true"]')].find((control) =>
      !control.disabled &&
      control.getAttribute('aria-hidden') !== 'true' &&
      !control.closest('[aria-hidden="true"]')
    );
    invalidControl?.focus();
  }
  removeBcpo() {
    this.addToCartForm.setAttribute('novalidate', 'novalidate');
    const node = this.addToCartForm.querySelector('#bcpo');
    const nodeValide = this.addToCartForm.querySelector('.bcpo-validation');
    node && node.remove();
    nodeValide && nodeValide.remove();
  }
  bcpoDomRemove() {
    if (!this.addToCartForm) return;
    this.removeBcpo();
    const observer = new MutationObserver((mutations, obs) => {
      const node = this.addToCartForm.querySelector('#bcpo');
      if (node) {
        this.removeBcpo();
        obs.disconnect();
      }
    });
    observer.observe(this.addToCartForm, {
      childList: true,
      subtree: true
    });
  }
  handlePropertiesSubmit() {
    if (!this.addToCartForm) return;
    if (this.boundAddToCartForm === this.addToCartForm) return;
    this.boundAddToCartForm = this.addToCartForm;
    this.addToCartForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!this.validateFieldRule()) {
        this.focusFirstInvalidField();
        return;
      }
      const customField = this.assembledFields.filter((option) => option.source === 0);
      let id = this.defaultOptions?.id;
      if (!id) { id = this.addToCartForm.querySelector('input[name="id"]')?.value; }
      const quantity = this.getCurrentMainQuantity();
      const formData = new FormData();
      formData.append('items[0][id]', id);
      formData.append('items[0][quantity]', `${quantity}`);
      const descriptions = customField.filter((f) => !f.hide && f.orderNote).map((f) => f.orderNote);
      if (descriptions?.length) {
        const description = descriptions.join(' -- ');
        formData.append('items[0][properties][_description]', description);
      }
      if (this._wrapper.tagName === 'TPS-PRODUCT-BUNDLED') {
        const bundledTitle = this._wrapper.querySelector('.tps-product-bundled__title');
        const bundledValue = bundledTitle.querySelector('.tps-product-bundled__value');
        const title = bundledTitle.dataset.name;
        const value = bundledValue.innerText;
        if (title && value) {
          formData.append(`items[0][properties][${title}]`, value);
          formData.append(`items[0][properties][_custom_type]`, 'bundled');
        }
      }
      for (const field of customField) {
        if (field.optionType === 'html') continue;
        if (field.hide) continue;
        const label = field.orderLabel || field.label;
        const value = field.optionType === 'date'
          ? this.formatCartDateValue(field.tps_value)
          : field.tps_value;
        if (field.optionType === 'size') {
          const { tps_width_value, tps_length_value, tps_width_fraction ,tps_length_fraction, tps_width_inches, tps_length_inches, tps_size_unit } = field;
          const widthLabel = field.config?.widthLabel?.trim() || 'Width';
          const lengthLabel = field.config?.lengthLabel?.trim() || 'Length';
          let width = `${tps_width_value}"`;
          let _width = `${tps_width_inches}"`;
          if (tps_width_fraction && !['-/-','0/0'].includes(tps_width_fraction)) {
            width = `${tps_width_value} ${tps_width_fraction}"`;
            _width = `${tps_width_inches} ${tps_width_fraction}"`
          }
          let length = `${tps_length_value}"`;
          let _length = `${tps_length_inches}"`;
          if (tps_length_fraction && !['-/-','0/0'].includes(tps_length_fraction)) {
            length = `${tps_length_value} ${tps_length_fraction}"`;
            _length = `${tps_length_inches} ${tps_length_fraction}"`;
          }
          formData.append('items[0][properties][_inches_size_width]', _width);
          formData.append('items[0][properties][_inches_size_length]', _length);
          if (tps_size_unit === 'cm') {
            width = `${tps_width_value}cm`;
            length = `${tps_length_value}cm`;
          }
          formData.append(`items[0][properties][${widthLabel}]`, width);
          formData.append(`items[0][properties][${lengthLabel}]`, length);
        } else if (field.optionType === 'file_upload') {
          const file = this.querySelector(`fieldset[data-key="${field.key}"] input[type="file"]`);
          if (!file.files[0]) continue;
          formData.append(`items[0][properties][${label}]`, file.files[0]);
        } else {
          if (!value) continue;
          formData.append(`items[0][properties][${label}]`, value);
        }
      }
      const selectedKeys = this.selectedKeys.filter(s => s.source === 0);
      let _tps = selectedKeys.map(s => s.key).join(',');
      const assembledFieldSize = this.assembledFields.filter((field) => field.optionType === 'size' && !field.hide);
      if (assembledFieldSize && assembledFieldSize.length > 0) {
        const sizePrice = assembledFieldSize.reduce((p, c) => {
          return p + this.computedSizePrice(c)
        }, 0);
        _tps += `${_tps ? ',' : ''}size:${this.encrypt(sizePrice / 100, 1000)}`;
      }
      const assembledNumber = this.assembledFields.filter((field) => field.optionType === 'number' && field.pricingRule && !field.hide);
      if (assembledNumber && assembledNumber.length > 0) {
        const numberPrice = assembledNumber.reduce((p, c) => {
          return p + this.computedNumberPrice(c);
        }, 0);
        _tps += `${_tps ? ',' : ''}number:${this.encrypt(numberPrice / 100, 1000)}`;
      }
      if (_tps) {
        _tps += `,tps:${this.encrypt(this._totalPrice / 100)}`;
        formData.append('items[0][properties][_tps]', _tps);
      }
      const sku_price = this.defaultOptions.price;
      if (sku_price >= 0) {
        formData.append('properties[_sku_price]', sku_price);
      }
      const pid = this._wrapper.dataset.pid;
      if (pid) {
        formData.append('items[0][properties][_pid]', pid);
      }
      const parentKeySeed = this.buildInstallationParentKeySeed(id);
      const installationResult = await this.getInstallationServiceItem(id, quantity, parentKeySeed);
      if (!installationResult.ok) {
        this.changeLoadingState('remove');
        return;
      }
      if (installationResult.item) {
        const mainServiceProperties = installationResult.item.properties || {};
        formData.append('items[0][properties][_svc_zip]', `${mainServiceProperties._svc_zip || ''}`);
        formData.append('items[0][properties][_svc_meta]', this.buildSvcMeta({
          kind: 'm',
          seed: parentKeySeed,
          serviceType: this.installationState.serviceType,
          variantId: `${installationResult.item.id}`,
          unitPrice: `${installationResult.item.unitPrice || ''}`
        }));
        formData.append('items[1][id]', `${installationResult.item.id}`);
        formData.append('items[1][quantity]', `${installationResult.item.quantity}`);
        const serviceProperties = installationResult.item.properties || {};
        for (const [key, value] of Object.entries(serviceProperties)) {
          formData.append(`items[1][properties][${key}]`, value);
        }
      }
      this.changeLoadingState();
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      }).then((response) => {
        if (!response.ok) {
          throw new Error('Add to cart failed');
        }
        return response.json();
      }).then(() => {
        window.location.href = '/cart';
      }).catch((error) => {
        console.error(error);
        this.changeLoadingState('remove');
      });
    });
  }
  updateBtnStatus() {
    const { top, height } = this.addToCartFormRect;
    const { innerHeight } = window;
    const srcollUp = top - (this.prevTop || 0) > 0;
    if (top <= 0) {
      this.addToCartForm.classList.add('add-to-cart-form__sticky');
    } else if (top > height && top < innerHeight - height) {
      this.addToCartForm.classList.remove('add-to-cart-form__sticky');
      this.isExposed = true;
    } else if (srcollUp && this.isExposed) {
      this.addToCartForm.classList.add('add-to-cart-form__sticky');
    } else {
      this.addToCartForm.classList.remove('add-to-cart-form__sticky');
    }
    this.prevTop = top;
  }
  observeAddToCart() {
    if (window.innerWidth > 768) return;
    this.updateBtnStatus();
    window.addEventListener('scroll', () => {
      if(this.ticking) return;
      requestAnimationFrame(() => {
        this.updateBtnStatus();
        this.ticking = false;
      });
      this.ticking = true;
    }, { passive: true });
  }
}
class TpsProductTemplate extends TpsProductParent {
  constructor() {
    super();
  }
  updateUrl() {
    const { id } = this.defaultOptions;
    if (!id) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', id);
    window.history.replaceState(null, '', url);
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("variant-change", (e) => {
      this.renderProductInfo(e.detail).then(() => {
        this.updateUrl();
      })
    });
    requestAnimationFrame(() => {
      this.skeleton?.remove();
      this.customFieldsData();
      this.initWebComponents();
    })
  }
}
customElements.define('tps-product-template', TpsProductTemplate);

class TpsProductBundled extends TpsProductParent {
  constructor() {
    super();
    this.abortController = null;
    this._cache = new Map();
    this.mediaRequestId = 0;
  }
  get bundle_handle() {
    let handle = new URLSearchParams(window.location.search).get('bundle');
    return this.returnHandle(handle);
  }
  get bundle_sku() {
    return new URLSearchParams(window.location.search).get('sku')
  }
  returnHandle(handle) {
    if (handle && this.querySelector(`custom-product-bundled-item input[data-handle="${handle}"]`)) {
      return handle;
    }
    const item = this.querySelector('custom-product-bundled-item input');
    return item?.dataset?.handle;
  }
  fetchProduct({ handle, sku = '' }) {
    if (this._cache.has(handle)) {
      return this._cache.get(handle);
    }
    this.abortController?.abort();
    this.abortController = new AbortController();
    const url = `/products/${handle}?view=bundled${sku ? `&variant=${sku}` : ''}`;
    const promise = fetch(url, {
      signal: this.abortController.signal
    })
    .then(res => res.text())
    .then(html => new DOMParser().parseFromString(html, 'text/html'))
    .catch(err => {                                                                               
      this._cache.delete(handle);
      return Promise.reject(err);
    })                                                                         
    this._cache.set(handle, promise);
    return promise;
  }
  updatePid(variantPickerDoc) {
    const pid = variantPickerDoc.dataset.pid;
    if (!pid) return;
    this.setAttribute('data-pid', pid);
  }
  updateUrlSku() {
    const url = new URL(window.location.href);
    const { id } = this.defaultOptions;
    if (!id) return;
    url.searchParams.set('sku', id);
    window.history.replaceState(null, '', url);
  }
  updateUrlHandle(handle) {
    if (!handle) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('sku');
    url.searchParams.set('bundle', handle);
    window.history.replaceState(null, '', url);
  }
  updateBundledValue({ target, value }) {
    const _wrapper = target.closest('fieldset');
    const bundledValue = _wrapper.querySelector('.tps-product-bundled__value');
    bundledValue.innerHTML = value;
  }
  updateVariantPickerLoading(isLoading) {
    const variantPicker = this.querySelector('.tps-bundled__content tps-variant-picker');
    if (!variantPicker) return;
    variantPicker.classList.toggle('is-loading', isLoading);
    variantPicker.toggleAttribute('inert', isLoading);
    variantPicker.setAttribute('aria-busy', String(isLoading));
  }
  connectedCallback() {
    super.connectedCallback();
    const handle = this.bundle_handle;
    const sku = this.bundle_sku;
    if (!handle) return;
    this.initCheckedItem(handle);
    this.loadProduct({ handle, sku, type: 'init' });
    this.addEventListener('update-field-bundle', (event) => {
      const { target } = event.detail;
      const value = target.value;
      const handle = target.dataset.handle;
      if (!value) return;
      this.updateUrlHandle(handle);
      this.updateBundledValue({ target, value });
      this.loadProduct({ handle, sku });
    });
    this.addEventListener("variant-change", (e) => {
      this.renderProductInfo(e.detail).then(() => {
        this.updateUrlSku();
      })
    });
  }
  loadSlideImgs(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.fetchPriority = 'high';
      img.loading = 'eager';
      img.src = url;
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  }
  updateMedia(mediaDoc, loadtype, mediaRequestId) {
    const slides = this.querySelector('tps-main-template-slides');
    if (!slides) return;
    const activeSlide = mediaDoc.querySelector('tps-main-template-slide.active');
    const currentImage = activeSlide.querySelector('.product-template__image img');
    if (loadtype === 'init' && currentImage) {
      const srcset = currentImage.srcset;
      const srcsetArr = srcset.split(/[, ]+/);
      const mobileUrl = srcsetArr.find(item => item.includes('width=560'));
      const pcUrl = srcsetArr.find(item => item.includes('width=800'));
      const url = window.innerWidth > 768 ? pcUrl : mobileUrl;
      this.loadSlideImgs(url).finally(() => {
        if (mediaRequestId !== this.mediaRequestId) return;
        slides.replaceWith(mediaDoc.cloneNode(true));
      });
    } else {
      slides.replaceWith(mediaDoc.cloneNode(true));
    }
  }
  updatePrice(priceDoc) {
    const prices = this.querySelectorAll('.tps-product-template__price');
    if (!prices.length) return;
    for (const price of prices) {
      price.replaceWith(priceDoc.cloneNode(true));
    }
  }
  updateVariant(variantPickerDoc) {
    const bundledContent = this.querySelector('.tps-bundled__content');
    if (!bundledContent) return;
    bundledContent.replaceWith(variantPickerDoc.cloneNode(true));
  }
  updateBuyButton(buyButtonDoc) {
    const buyButton = this.querySelector('form.add-to-cart-form');
    if (!buyButton) return;
    buyButton.replaceWith(buyButtonDoc.cloneNode(true));
  }
  updateAddSwatch(buyAddSwatch) {
    const addSwatch = this.querySelector('.tps-add-swatch-to-cart-wrapper');
    if (!addSwatch) return;
    if (!buyAddSwatch) {
      addSwatch.replaceChildren();
      return;
    }
    addSwatch.replaceWith(buyAddSwatch.cloneNode(true));
  }
  loadProduct({ handle, sku, type = '' }) {
    this.updateAddToCartButtonState(true);
    this.updateVariantPickerLoading(true);
    const mediaRequestId = ++this.mediaRequestId;
    this.fetchProduct({ handle, sku }).then((htmlDoc) => {
      if (mediaRequestId !== this.mediaRequestId) return;
      const mediaDoc = htmlDoc.querySelector('tps-main-template-slides');
      const priceDoc = htmlDoc.querySelector('.tps-product-template__price');
      const variantPickerDoc = htmlDoc.querySelector('.tps-bundled__content');
      const buyButtonDoc = htmlDoc.querySelector('form.add-to-cart-form');
      const buyAddSwatch = htmlDoc.querySelector('.tps-add-swatch-to-cart-wrapper');
      this.updateMedia(mediaDoc, type, mediaRequestId);
      this.updatePrice(priceDoc);
      this.updatePid(variantPickerDoc);
      this.updateVariant(variantPickerDoc);
      this.updateBuyButton(buyButtonDoc);
      this.updateAddSwatch(buyAddSwatch);
      this.customFieldsData();
      this.initWebComponents();
    }).catch((error) => {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    }).finally(() => {
      if (mediaRequestId !== this.mediaRequestId) return;
      this.updateVariantPickerLoading(false);
    })
  }
  initCheckedItem(handle) {
    const item = this.querySelector(`custom-product-bundled-item input[data-handle="${handle}"]`);
    if (!item) return;
    item.checked = true;
    this.updateBundledValue({ target: item, value: item.value });
  }
}
customElements.define('tps-product-bundled', TpsProductBundled);

class CustomProductBundledItem extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
  }
  
  connectedCallback() {
    this.addEventListener('change', this.onChange);
  }
  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }
  onChange(event) {
    this.dispatchEvent(new CustomEvent('update-field-bundle', {
      detail: { target: event.target },
      bubbles: true
    }));
  }
  
}
customElements.define('custom-product-bundled-item', CustomProductBundledItem);

class TpsProductTemplateSlide extends HTMLElement {
  constructor() {
    super();
    this.slideFlickity = null;
    this.slideObserve = null;
    this.type = this.dataset.type || 'normal';
    this.slides = this.querySelectorAll('.product-template__image');
    this.videos = this.querySelectorAll('.product-template__thumb-video');
    this.photoZoomIcon = this.querySelector('.product-template__photo-zoom');
    this.seeMore = this.querySelector('.tps-see-more');
    this.scale = 2;
    this.mouseMaskStyle = {
      width: 120,
      height: 160
    };
  }
  connectedCallback() {
    if (this.type === 'init') return;
    if (this.classList.contains('active')) {
      this.createSwiper();
    }
    this.slideObserve = new MutationObserver(() => {
      this.classList.contains('active') ? this.createSwiper() : this.destroySwiper();
    });
    this.slideObserve.observe(this, { attributes: true, attributeFilter: ['class'] });
    this.observeVideo();
    this.initImageZoom();
    this.initMouseMask();
  }
  disconnectedCallback() {
    this.destroySwiper();
    this.slideObserve?.disconnect();
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.mouseMask) {
      this.mouseMask.remove();
    }
  }
  handleScrollThumb({ index }) {
    this.querySelector('.product-template__thumb.is-active').classList.remove('is-active');
    const newThumb = this.querySelector(`.product-template__thumb[data-index='${index}']`);
    newThumb.classList.add('is-active');
    if (window.innerWidth <= 768) {
      newThumb.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
  }
  createSwiper() {
    if (this.slideFlickity) {
      this.slideFlickity.destroy();
      this.slideFlickity = null;
    }
    const mainSlide = this.querySelector('.product-template__images');
    this.slideFlickity = new Flickity(mainSlide, {
      draggable: true,
      wrapAround: true,
      prevNextButtons: false,
      pageDots: false,
      cellAlign: 'left',
      contain: true,
      friction: 0.8,
      selectedAttraction: 0.2,
      freeScroll: false,
      adaptiveHeight: false,
      lazyLoad: 1,
      accessibility: false,
      rightToLeft: false,
      on: {
        change: (index) => this.handleScrollThumb({ index })
      }
    });
    const thumbs = this.querySelectorAll('.product-template__thumb');
    for (const thumb of thumbs) {
      thumb.addEventListener('click', () => {
        if (thumb.classList.contains('is-active')) return;
        this.querySelector('.product-template__thumb.is-active').classList.remove('is-active');
        thumb.classList.add('is-active');
        const index = thumb.dataset.index;
        this.slideFlickity.select(index, true, true);
        window.scrollTo({
          top: window.innerWidth > 768 ? 130 : 140,
          behavior: 'smooth'
        });
      });
    }
  }
  destroySwiper() {
    if (this.slideFlickity) {
      this.slideFlickity.destroy();
      this.slideFlickity = null;
    }
  }
  observeVideo() {
    for (const video of this.videos) {
      new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          entry.isIntersecting ? video.play() : video.pause();
        });
      }, { threshold: [0.3], delay: 0 }).observe(video);
    }
  }
  initImageZoom() {
    this.photoZoomIcon && (this.photoZoomIcon.addEventListener('click', () => {
      const index = this.querySelector('.product-template__image.is-selected').dataset.index;
      this.createZoom({ index, trigger: this.photoZoomIcon });
    }));
    this.seeMore && (this.seeMore.addEventListener('click', () => {
      this.createZoom({ index: 6 });
    }));
  }
  createZoom({ index, trigger }) {
    const items = [...this.slides].map(slide => {
      const video = slide.querySelector('video');
      const img = slide.querySelector('img');
      const width = window.innerWidth > 768 ? 1200 : 500;
      const height = window.innerWidth > 768 ? 1200 : 500;
      if (video) {
        const videoClone = video.cloneNode(true);
        const vidoeStype = `width: ${width}px; height: ${height}px; max-width: 100%; max-height: 100%;`;
        videoClone.style = vidoeStype;
        const videoHtml = videoClone.outerHTML;
        return {
          html: `<div class="pswp__zoom-video-wrap" style="position: relative; display: flex; justify-content: center; align-items: center;height: 100%;">
            <img src="${video.poster}" class="pswp__zoom-video-img" style="position: absolute;top: 50%; left: 50%; transform: translate(-50%, -50%); object-fit: contain; ${vidoeStype}" />
            ${videoHtml}
          </div>`,
          w: width,
          h: height,
          el: video,
          type: 'video',
          initialZoomLevel: 1
        }
      } else {
        const msrc = img.src;
        const src = msrc.split('?')[0];
        return {
          msrc: msrc,
          src: src,
          w: width,
          h: height,
          el: img,
          type: 'image',
          initialZoomLevel: 0.5
        }
      }
    });
    this.initGallery(items, index, trigger);
  }
  initGallery(items, index, trigger) {
    var pswpElement = document.querySelectorAll('.pswp')[0];
    var options = {
      allowPanToNext: false,
      captionEl: false,
      closeOnScroll: false,
      counterEl: false,
      history: false,
      index: index * 1,
      pinchToClose: false,
      preloaderEl: false,
      scaleMode: 'zoom',
      shareEl: false,
      tapToToggleControls: false,
      getThumbBoundsFn: function (index) {
        var pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
        var thumbnail = items[index].el;
        var rect = thumbnail.getBoundingClientRect();
        return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
      }
    }
    this.gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
    this.gallery.listen('destroy', () => {
      trigger?.focus();
    });
    this.gallery.listen('afterChange', (event) => {
      const cindex = this.gallery.getCurrentIndex();
      this.slideFlickity.select(cindex, true, true);
      const currItem = this.gallery.currItem;
      let zoomVideo = null;
      if (currItem.type === 'video') {
        const zoomVideo = currItem.container.querySelector('video');
        zoomVideo.play();
        zoomVideo.addEventListener('canplay', () => {
          zoomVideo.closest('.pswp__zoom-video-wrap').querySelector('img').classList.add('hide');
        });
      } else {
        if (zoomVideo) {
          zoomVideo.pause();
          zoomVideo = null;
        }
      }
    });
    this.gallery.init();
  }
  initMouseMask() {
    if (this.dataset.mouseMask !== 'open') return;
    if (window.innerWidth <= 768) return;
    const productImages = this.querySelectorAll('.product-template__image');
    for (const productImage of productImages) {
      this.mouseMaskEvent(productImage);
    }
  }
  mouseMaskEvent(container) {
    if (this.mouseMask) return;
    let containerRect = container.getBoundingClientRect();
    const throttledMouseMove = theme.utils.throttle(16, (event) => {
      if (!this.isMouseOver) return;
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
      }
      this.animationFrameId = requestAnimationFrame(() => {
        this.updateMaskPosition(event, containerRect);
      });
    });
    container.addEventListener('mouseenter', (e) => {
      containerRect = container.getBoundingClientRect();
      if (this.mouseMask) return;
      this.isMouseOver = true;
      this.createMouseMask(container);
      this.createScaleMask(container);
      this.updateMaskPosition(e, containerRect);
    });
    container.addEventListener('mousemove', throttledMouseMove);
    container.addEventListener('mouseleave', () => {
      this.isMouseOver = false;
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      this.removeMouseMask();
      this.removeScaleMask();
    });
  }
  updateMaskPosition(event, containerRect) {
    if (!this.mouseMask || !this.isMouseOver) return;

    const x = event.clientX - containerRect.left;
    const y = event.clientY - containerRect.top;

    const maskRect = this.mouseMask.getBoundingClientRect();
    const maskWidth = maskRect.width || 90;
    const maskHeight = maskRect.height || 90;
    const halfWidth = maskWidth / 2;
    const halfHeight = maskHeight / 2;

    const minX = halfWidth;
    const maxX = containerRect.width - halfWidth;
    const minY = halfHeight;
    const maxY = containerRect.height - halfHeight;

    const clampedX = Math.max(minX, Math.min(x, maxX));
    const clampedY = Math.max(minY, Math.min(y, maxY));

    this.mouseMask.style.transform = `translate(${clampedX}px, ${clampedY}px) translate(-50%, -50%)`;
    if (this.scaleMask) {
      const relativeX = (clampedX - halfWidth) / (containerRect.width - maskWidth);
      const relativeY = (clampedY - halfHeight) / (containerRect.height - maskHeight);

      const scaleImage = this.scaleMask.querySelector('img');
      if (scaleImage) {
        const scaleMaskWidth = this.mouseMaskStyle.width * this.scale;
        const scaleMaskHeight = this.mouseMaskStyle.height * this.scale;
        const imageWidth = containerRect.width * this.scale;
        const imageHeight = containerRect.height * this.scale;

        const maxMoveX = imageWidth - scaleMaskWidth;
        const maxMoveY = imageHeight - scaleMaskHeight;

        const moveX = -relativeX * maxMoveX;
        const moveY = -relativeY * maxMoveY;

        scaleImage.style.transform = `translate(${moveX}px, ${moveY}px)`;
      }
    }
  }
  createScaleMask(container) {
    if (this.scaleMask) {
      this.scaleMask.remove();
      this.scaleMask = null;
    }
    const containerRect = container.getBoundingClientRect();
    const img = container.querySelector('img');
    const currentSrc = img.src;
    const src = currentSrc.split('?')[0];
    this.scaleMask = document.createElement('div');
    this.scaleMask.classList.add('product-template__mouse-scale');
    this.scaleMask.style.width = `${this.mouseMaskStyle.width * this.scale}px`;
    this.scaleMask.style.height = `${this.mouseMaskStyle.height * this.scale}px`;
    const scaleImage = document.createElement('img');
    scaleImage.src = currentSrc;
    scaleImage.style.width = `${containerRect.width * this.scale}px`;
    scaleImage.style.height = `${containerRect.height * this.scale}px`;
    scaleImage.style.maxWidth = 'none';
    this.scaleMask.appendChild(scaleImage);
    this.appendChild(this.scaleMask);
    this.loadSlideImgs(src).then(() => {
      scaleImage.src = src;
    })
  }
  loadSlideImgs(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = url;
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  }
  removeScaleMask() {
    if (!this.scaleMask) return;
    this.scaleMask.remove();
    this.scaleMask = null;
  }
  createMouseMask(container) {
    this.mouseMask = document.createElement('div');
    this.mouseMask.classList.add('product-template__mouse-mask');
    this.mouseMask.style.width = `${this.mouseMaskStyle.width}px`;
    this.mouseMask.style.height = `${this.mouseMaskStyle.height}px`;
    container.appendChild(this.mouseMask);
  }
  removeMouseMask() {
    if (!this.mouseMask) return;
    this.mouseMask.remove();
    this.mouseMask = null;
  }
}
customElements.define('tps-main-template-slide', TpsProductTemplateSlide);

class TpsProductTemplateSlides extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.initFetchFirstImage();
  }
  loadSlideImgs(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = url;
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  }
  fetchImages(urls) {
    const fetchImages = urls.map((url) => this.loadSlideImgs(url));
    return Promise.all(fetchImages);
  }
  initFetchThumbImgs() {
    const slides = this.querySelectorAll('tps-main-template-slide:not(.active)');
    for (const slide of slides) {
      const thumbs = slide.querySelectorAll('.product-template__thumb:nth-of-type(-n+4)');
      for (const thumb of thumbs) {
        const img = thumb.querySelector('img');
        const url = img.src;
        const minUrl = url.replace('width=360', 'width=12');
        thumb.style.backgroundImage = `url(${minUrl})`;
        this.loadSlideImgs(minUrl).then(() => {
          thumb.style.backgroundImage = `url(${minUrl})`;
        })
      }
    }
  }
  loadFirstImage(pcUrls, mobileUrls) {
    setTimeout(() => {
      if (window.innerWidth > 768) {
        this.fetchImages(pcUrls);
      } else {
        this.fetchImages(mobileUrls);
      }
      this.initFetchThumbImgs();
    }, 8000);
  }
  initFetchFirstImage() {
    const firstImages = this.querySelectorAll('tps-main-template-slide:not(.active) .product-template__image[data-index="0"]');
    const mobileUrls = [];
    const pcUrls = [];
    for (const firstImage of firstImages) {
      const img = firstImage.querySelector('img');
      const srcset = img.srcset;
      const srcsetArr = srcset.split(/[, ]+/);
      const mobileUrl = srcsetArr.find(item => item.includes('width=560'));
      const pcUrl = srcsetArr.find(item => item.includes('width=800'));
      mobileUrls.push(mobileUrl);
      pcUrls.push(pcUrl);
    }
    const firstActiveImages = this.querySelector('tps-main-template-slide.active .product-template__image[data-index="0"] img');
    if (firstActiveImages) {
      const isImageLoaded = firstActiveImages.complete && firstActiveImages.naturalWidth > 0;
      if (isImageLoaded) {
        this.loadFirstImage(pcUrls, mobileUrls);
        return;
      }
      firstActiveImages.addEventListener('load', () => {
        this.loadFirstImage(pcUrls, mobileUrls);
      });
    }
  }
}
customElements.define('tps-main-template-slides', TpsProductTemplateSlides);

class TpsProductQuestionList extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.currentTrigger = null;
    this._parent = null;
    this.url = window.TPS_PRODUCT_COLLAPSE_DATA_URL;
    this.lists = [];
    this.listByKey = new Map();
    this.listByText = new Map();
    this.a11y = null;
    this.accordionItemCount = 0;
    TpsProductQuestionList.instanceCount = (TpsProductQuestionList.instanceCount || 0) + 1;
    this.instanceId = `tps-product-question-${TpsProductQuestionList.instanceCount}`;
    this.handleParentClick = this.handleParentClick.bind(this);
  }
  normalizeText(text = '') {
    return text
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[\n\r]/g, ' ')
      .replace(/[：]/g, ':')
      .replace(/[’]/g, "'")
      .replace(/[“”]/g, '"');
  }
  normalizeMode(mode = '') {
    return `${mode || ''}`.trim().toLowerCase();
  }
  normalizeKey(key = '') {
    return this.normalizeText(key).toLowerCase();
  }
  normalizeItem(item = {}) {
    return {
      ...item,
      key: this.normalizeKey(item.key || ''),
      text: this.normalizeText(item.text || ''),
      title: `${item.title || ''}`.trim(),
      mode: this.normalizeMode(item.mode || 'collapse'),
      children: Array.isArray(item.children) ? item.children : []
    };
  }
  buildIndexes() {
    this.listByKey = new Map();
    this.listByText = new Map();
    this.lists.forEach((item) => {
      if (item.key && !this.listByKey.has(item.key)) {
        this.listByKey.set(item.key, item);
      }
      if (item.text && !this.listByText.has(item.text)) {
        this.listByText.set(item.text, item);
      }
    });
  }
  loadQuestion() {
    if (!this.url) return;
    fetch(this.url).then(response => {
      return response.json();
    }).then(data => {
      this.lists = Array.isArray(data) ? data.map((item) => this.normalizeItem(item)) : [];
      this.buildIndexes();
    }).catch(() => {
      this.lists = [];
      this.buildIndexes();
    });
  }
  resolveEntryNode(ref = '') {
    const normalizedText = this.normalizeText(ref);
    const normalizedKey = this.normalizeKey(ref);
    if (!normalizedText && !normalizedKey) return null;
    return this.listByKey.get(normalizedKey)
      || this.listByText.get(normalizedText)
      || this.lists.find((item) => item.key === normalizedKey || item.text === normalizedText)
      || null;
  }
  findMatchedItem(key) {
    return this.resolveEntryNode(key);
  }
  resolveNode(item = null) {
    if (!item) return null;
    if (typeof item === 'string' || typeof item === 'number') {
      return this.resolveEntryNode(item);
    }
    return this.normalizeItem(item);
  }
  getChildrenNodes(item = {}) {
    const children = Array.isArray(item.children) ? item.children : [];
    return children
      .map((child) => this.resolveNode(child))
      .filter(Boolean);
  }
  getDisplayTitle(node = {}, mode = '', depth = 0) {
    if (mode === 'tree' && depth === 0 && node.asSecondTitle) {
      return `${node.asSecondTitle}`.trim();
    }
    return `${node.title || ''}`.trim();
  }
  renderHeader(item = {}) {
    const headerEl = this.shadowRoot.querySelector('#main-title');
    const subtitleEl = this.shadowRoot.querySelector('#main-subtitle');
    const popup = this.shadowRoot.querySelector('.popup');
    const title = item.title || '';
    const subtitle = item.subtitle || '';

    if (headerEl) headerEl.textContent = title;
    if (subtitleEl) {
      if (subtitle) {
        subtitleEl.innerHTML = subtitle;
        subtitleEl.hidden = false;
      } else {
        subtitleEl.innerHTML = '';
        subtitleEl.hidden = true;
      }
    }
    if (popup) {
      if (subtitle) {
        popup.setAttribute('aria-describedby', 'main-subtitle');
      } else {
        popup.removeAttribute('aria-describedby');
      }
    }
  }
  renderAccordion(items, mode, depth = 0) {
    const container = this.shadowRoot.querySelector('.accordion');
    if (!container) return;
    container.innerHTML = '';
    this.accordionItemCount = 0;
    this.renderAccordionItems(container, items, mode, depth);
  }
  renderAccordionItems(container, items, mode, depth = 0) {
    if (!container || !Array.isArray(items)) return;

    const arrowSvg = `
      <svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M15 7.5L10 12.5L5 7.5" stroke="#666" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    items.forEach((item, index) => {
      const node = this.resolveNode(item);
      if (!node) return;
      const inheritedMode = this.normalizeMode(mode || 'collapse');
      const nodeMode = inheritedMode === 'tree' && depth === 0
        ? 'tree'
        : inheritedMode === 'single'
          ? 'single'
        : this.normalizeMode(node.mode || mode || 'collapse');
      const childNodes = this.getChildrenNodes(node);
      const itemEl = document.createElement('div');
      itemEl.className = 'accordion-item';
      itemEl.dataset.depth = `${depth}`;
      itemEl.dataset.mode = nodeMode;

      if (nodeMode === 'single') {
        itemEl.classList.add('open', 'accordion-item--single');
        itemEl.innerHTML = `
          <div class="accordion-content" aria-hidden="false">${node.content || ''}</div>
        `;
        container.appendChild(itemEl);
        return;
      }

      const displayTitle = this.getDisplayTitle(node, mode, depth);
      const showTitle = Boolean(displayTitle);
      const itemIdentifier = `${this.instanceId}-${this.accordionItemCount++}`;
      const triggerId = `${itemIdentifier}-trigger`;
      const panelId = `${itemIdentifier}-panel`;
      const isOpen = depth === 0 && index === 0;
      itemEl.classList.add(`accordion-item--${nodeMode}`);
      itemEl.innerHTML = `
        ${showTitle ? `
          <button
            type="button"
            id="${triggerId}"
            class="accordion-title"
            aria-expanded="${isOpen}"
            aria-controls="${panelId}"
          >
            ${displayTitle}
            <span class="arrow" aria-hidden="true">${arrowSvg}</span>
          </button>
        ` : ''}
        <div
          class="accordion-content"
          id="${panelId}"
          ${showTitle ? `role="region" aria-labelledby="${triggerId}"` : ''}
          aria-hidden="${showTitle ? !isOpen : false}"
          ${showTitle && !isOpen ? 'inert' : ''}
        >
          ${node.content ? `<div class="accordion-content__body">${node.content}</div>` : ''}
          <div class="accordion-content__children"></div>
        </div>
      `;

      const titleEl = itemEl.querySelector('.accordion-title');
      const childrenContainer = itemEl.querySelector('.accordion-content__children');
      if (childNodes.length && childrenContainer) {
        this.renderAccordionItems(childrenContainer, childNodes, nodeMode, depth + 1);
      }
      if (showTitle) {
        itemEl.classList.add('has-title');
        itemEl.classList.toggle('open', isOpen);
        titleEl.addEventListener('click', () => {
          this.setAccordionItemExpanded(itemEl, !itemEl.classList.contains('open'));
        });
      } else {
        itemEl.classList.add('open');
      }
      container.appendChild(itemEl);
    });
  }
  setAccordionItemExpanded(item, expanded) {
    const trigger = item.querySelector(':scope > .accordion-title');
    const panel = item.querySelector(':scope > .accordion-content');
    if (!trigger || !panel) return;
    item.classList.toggle('open', expanded);
    trigger.setAttribute('aria-expanded', `${expanded}`);
    panel.setAttribute('aria-hidden', `${!expanded}`);
    panel.toggleAttribute('inert', !expanded);
  }
  renderEntry(item) {
    const container = this.shadowRoot.querySelector('.accordion');
    if (!container) return;
    const normalizedItem = this.resolveNode(item);
    if (!normalizedItem) return;

    container.innerHTML = '';
    const mode = this.normalizeMode(normalizedItem.mode || 'collapse');
    const children = this.getChildrenNodes(normalizedItem);

    if (mode === 'single') {
      this.renderAccordionItems(container, children.length ? children : [normalizedItem], mode, 0);
      return;
    }

    if (children.length) {
      this.renderAccordionItems(container, children, mode, 0);
      return;
    }

    if (normalizedItem.content) {
      this.renderAccordionItems(container, [normalizedItem], 'single', 0);
    }
  }
  showPopup(item) {
    const normalizedItem = this.resolveNode(item);
    if (!normalizedItem) return;

    this.renderHeader(normalizedItem);
    this.renderEntry(normalizedItem);

    const popup = this.shadowRoot.querySelector('.popup');
    const overlay = this.shadowRoot.querySelector('.overlay');

    overlay.classList.add('active');
    popup.classList.add('active');
    this.a11y?.open(this.currentTrigger);
  }
  hidePopup() {
    const popup = this.shadowRoot.querySelector('.popup');
    const overlay = this.shadowRoot.querySelector('.overlay');

    this.a11y?.close();
    popup.classList.remove('active');
    overlay.classList.remove('active');
    this.currentTrigger = null;
  }
  render() {
    const template = document.getElementById('tps-product-collapse-popup-template');
    if (!template) return;
    const content = template.content.cloneNode(true);
    this.shadowRoot.innerHTML = '';
    this.shadowRoot.appendChild(content);

    this.a11y = new TpsProductQuestionListA11y(this, () => this.hidePopup());
    const closeBtn = this.shadowRoot.querySelector('.close-btn');
    const overlay = this.shadowRoot.querySelector('.overlay');
    closeBtn.onclick = () => this.hidePopup();
    overlay.onclick = () => this.hidePopup();
  }
  connectedCallback() {
    this._parent = this.closest('.tps-product-template');
    this.loadQuestion();
    this.render();
    this._parent?.addEventListener('click', this.handleParentClick);
  }
  disconnectedCallback() {
    this._parent?.removeEventListener('click', this.handleParentClick);
    this.a11y?.destroy();
  }
  handleParentClick(event) {
    const target = event.target.closest('.legend-label_question');
    if (!target) return;
    const lookupValue = target.dataset.key || target.dataset.text || target.textContent || '';
    if (!lookupValue) return;
    const matchedItem = this.resolveEntryNode(lookupValue);
    if (!matchedItem) return;
    event.preventDefault();
    this.currentTrigger = target;
    this.showPopup(matchedItem);
  }
}
customElements.define('tps-product-question-list', TpsProductQuestionList);

class TpsVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.section_id = this.dataset.sectionId;
    this.url = this.dataset.productUrl;
  }
  get defaultOptions() {
    return Array.from(this.querySelectorAll('fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
  connectedCallback() {
    this.addEventListener('change', (event) => {
      this.variantChanged(event);
    });
  }
  disconnectedCallback() {}
  variantChanged(event) {
    this.dispatchEvent(
      new CustomEvent("variant-change", {
        detail: {
          requestUrl: this.buildRequestUrlWithParams(),
          target: event.target
        },
        bubbles: true,
        composed: true
      })
    );
  }
  buildRequestUrlWithParams() {
    const params = [];
    // params.push(`section_id=${this.section_id}`);
    params.push(`view=bundled`);
    if (this.defaultOptions.length) {
      params.push(`option_values=${this.defaultOptions.join(',')}`);
    }
    return `${this.url}?${params.join('&')}`;
  }
}
customElements.define('tps-variant-picker', TpsVariantPicker);

class TpsFieldText extends HTMLElement {
  constructor() {
    super();
    this.wrapper = this.closest('.tps-fields');
    this.required = this.dataset.required || false;
    this.input = this.querySelector('input');
    this.field = this.closest('div');
    // this._parent = this.closest('fieldset').dataset.key;
    this.error = null;
  }
  get value() {
    // return [{ key: this._parent, value: this.input.value }]
    return this.input.value;
  }
  createError(text) {
    this.clearError();
    const result = TpsFieldDateA11y.showFieldError(this.input, this.field, text);
    this.error = result.error;
  }
  clearError() {
    const errorId = this.error?.id || `${this.input.id || this.input.name}-error`;
    TpsFieldDateA11y.clearFieldError(this.input, errorId);
    this.error = null;
  }
  validateRule() {
    const value = this.input.value;
    if (this.required && !value.trim()) {
      const message = 'Please fill in field';
      this.createError(message);
      return {
        ok: false,
        value,
        message
      }
    } else {
      this.clearError();
      return {
        ok: true,
        value
      }
    }
  }
  connectedCallback() {
    this.input.addEventListener('input', (event) => {
      this.dispatchEvent(new CustomEvent('update-field-input', { bubbles: true }));
    });
  }
}
customElements.define('tps-field-text', TpsFieldText);

class TpsFieldDate extends HTMLElement {
  constructor() {
    super();
    this.required = this.dataset.required || false;
    this.displayFormat = 'MM/DD/YYYY';
    this.startOffset = this.parseInteger(this.dataset.startOffset, 0);
    this.maxSelectableUnits = this.parseInteger(this.dataset.maxSelectableUnits, null);
    this.disabledWeekdays = this.parseArray(this.dataset.disabledWeekdays)
      .map((day) => Number(day))
      .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);
    this.disabledDates = new Set(this.parseArray(this.dataset.disabledDates));
    this.input = this.querySelector('input');
    this.picker = null;
    this.a11y = new TpsFieldDateA11y(this);
  }
  get value() {
    return this.input.value;
  }
  parseInteger(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback;
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  
  parseArray(value) {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }
  flatpickrDateFormat() {
    const tokenMap = {
      YYYY: 'Y',
      YY: 'y',
      MM: 'm',
      DD: 'd'
    };
    return this.displayFormat.replace(/YYYY|YY|MM|DD/g, (token) => tokenMap[token] || token);
  }
  today() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }
  addDays(date, days) {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
  }
  formatDate(date) {
    const year = `${date.getFullYear()}`;
    const tokenMap = {
      YYYY: year,
      YY: year.slice(-2),
      MM: `${date.getMonth() + 1}`.padStart(2, '0'),
      DD: `${date.getDate()}`.padStart(2, '0')
    };
    return this.displayFormat.replace(/YYYY|YY|MM|DD/g, (token) => tokenMap[token] || token);
  }
  isDisabledDate(date) {
    return this.disabledWeekdays.includes(date.getDay()) || this.disabledDates.has(this.formatDate(date));
  }
  createError(text) {
    this.a11y.showError(text);
  }
  validateRule() {
    const value = this.input.value;
    if (this.required && !value.trim()) {
      const message = 'Please select a date';
      this.createError(message);
      return {
        ok: false,
        value,
        message
      }
    }
    this.a11y.clearError();
    return {
      ok: true,
      value
    }
  }
  updateValue() {
    this.validateRule();
    this.dispatchEvent(new CustomEvent('update-field-input', { bubbles: true }));
  }
  connectedCallback() {
    const minDate = this.addDays(this.today(), this.startOffset);
    const afterTime = this.startOffset + this.maxSelectableUnits - 1;
    const maxDate = this.addDays(this.today(), afterTime);
    const objDate = {};
    if (this.maxSelectableUnits > 0) {
      objDate.minDate = minDate;
      objDate.maxDate = maxDate;
    }
    if (window.flatpickr) {
      this.picker = window.flatpickr(this.input, {
        dateFormat: this.flatpickrDateFormat(),
        ...objDate,
        disableMobile: true,
        disable: [
          (date) => this.isDisabledDate(date)
        ],
        animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        onReady: (_selectedDates, _dateString, picker) => this.a11y.connectPicker(picker),
        onOpen: () => this.a11y.setExpanded(true),
        onClose: () => this.a11y.setExpanded(false),
        onChange: () => this.updateValue()
      });
    }
  }
  disconnectedCallback() {
    this.a11y.destroy();
    this.picker?.destroy();
  }
}
customElements.define('tps-field-date', TpsFieldDate);

class TpsFieldNumber extends HTMLElement {
  constructor() {
    super();
    this.isComposing = false;
    this.min = this.getAttribute('min') || 0;
    this.max = this.getAttribute('max');
    this.required = this.dataset.required || false;
    // this._parent = this.closest('fieldset').dataset.key;
    this.input = this.querySelector('input');
    this.field = this.closest('div');
    this.error = null;
  }
  get value() {
    // return [{ key: this._parent, value: this.input.value }];
    return this.input.value;
  }
  set value(value) {
    this.input.value = value;
  }
  createError(text) {
    this.clearError();
    const result = TpsFieldDateA11y.showFieldError(this.input, this.field, text);
    this.error = result.error;
  }
  clearError() {
    const errorId = this.error?.id || `${this.input.id || this.input.name}-error`;
    TpsFieldDateA11y.clearFieldError(this.input, errorId);
    this.error = null;
  }
  formatInput() {
    const { value } = this.validateRule();
    this.value = value;
    this.dispatchEvent(new CustomEvent('update-field-input', {
      bubbles: true
    }));
  }
  normalizeInteger(value) {
    return value.replace(/[\uFF10-\uFF19]/g, s =>
      String.fromCharCode(s.charCodeAt(0) - 0xFEE0)
    ).replace(/\D/g, '');
  }
  validateRule() {
    const value = this.normalizeInteger(this.input.value);
    let num = parseInt(value, 10);
    if (this.required && isNaN(num)) {
      const message = 'Please fill in field';
      this.createError(message);
      return {
        ok: false,
        value: '',
        message
      }
    } else if (num > 0 && (num < this.min || num > this.max)) {
      const message = `must be between ${this.min} and ${this.max}.`;
      this.createError(message);
      return {
        ok: false,
        value: num,
        message
      }
    } else {
      this.clearError();
      return {
        ok: true,
        value
      }
    }
  }
  connectedCallback() {
    this.input.addEventListener('compositionstart', () => {
      this.isComposing = true;
    });
    this.input.addEventListener('compositionend', (e) => {
      this.isComposing = false;
      this.formatInput();
    });
    this.input.addEventListener('input', (e) => {
      if (this.isComposing) return;
      this.formatInput();
    });
  }
}
customElements.define('tps-field-number', TpsFieldNumber);

class TpsFieldSize extends HTMLElement {
  constructor() {
    super();
    this.key = this.closest('fieldset').dataset.key;
    this.allowFraction = this.dataset.allowFraction === 'true';
    this.required = this.dataset.required || true;
    this.sizeInputs = this.querySelectorAll('.tps-size-field-input input');
    this.sizeWidth = this.querySelector('.tps-size-field-input input[data-type="width"]');
    this.sizeLength = this.querySelector('.tps-size-field-input input[data-type="length"]');
    this.widthFractionSelect = this.querySelector('tps-field-select[data-select-type="width"]');
    this.lengthFractionSelect = this.querySelector('tps-field-select[data-select-type="length"]');
    this.unitBtns = this.querySelectorAll('.tps-unit-btn');
    this.unitLabels = this.querySelectorAll('.tps-unit-label');
    this.enableCm = this.dataset.enableCm === 'true';
    this.sizeUnit = 'inches';
  }
  get sizeValidationRule() {
    return this.closest('.tps-product-template').fields?.find((field) => field.optionKey === this.key);
  }
  getDimensionLabel(type) {
    const label = type === 'width' ? this.dataset.widthLabel : this.dataset.lengthLabel;
    const fallback = type === 'width' ? 'Width' : 'Length';
    return this.escapeHtml(label?.trim() || fallback);
  }
  escapeHtml(value = '') {
    return `${value}`
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  get widthInputValue() {
    return this.sizeWidth.value;
  }
  get lengthInputValue() {
    return this.sizeLength.value;
  }
  set widthInputValue(value) {
    this.sizeWidth.value = value;
  }
  set lengthInputValue(value) {
    this.sizeLength.value = value;
  }
  get widthFractionSelectValue() {
    return this.widthFractionSelect ? this.widthFractionSelect.value[0].value : '';
  }
  get lengthFractionSelectValue() {
    return this.lengthFractionSelect ? this.lengthFractionSelect.value[0].value : '';
  }
  set widthFractionSelectValue(value = '-/-') {
    this.setFractionSelectValue(this.widthFractionSelect, value);
    this.widthFractionSelect.value = value;
  }
  set lengthFractionSelectValue(value = '-/-') {
    this.setFractionSelectValue(this.lengthFractionSelect, value);
    this.lengthFractionSelect.value = value;
  }
  get widthInchesValue() {
    return this.sizeUnit === 'inches' ? this.widthInputValue : this.cmToInches(this.widthInputValue, 1).value;
  }
  get lengthInchesValue() {
    return this.sizeUnit === 'inches' ? this.lengthInputValue : this.cmToInches(this.lengthInputValue, 1).value;
  }
  normalizeInteger(value = '') {
    const normalized = String(value)
      .replace(/[０-９]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
      .replace(/[eE+-]/g, '');
    if (this.sizeUnit === 'cm') {
      const cleaned = normalized.replace(/[^\d.]/g, '');
      const match = cleaned.match(/^\d+(?:\.\d)?/);
      return match ? match[0] : '';
    }
    return normalized.replace(/\D/g, '');
  }
  shouldRewriteInput(value = '') {
    const current = String(value ?? '');
    if (this.sizeUnit === 'cm') {
      return !/^\d+$|^\d+\.\d$/.test(current);
    }
    return true;
  }
  toComparableNumber(value = '') {
    const normalized = this.normalizeInteger(value);
    if (normalized === '') return NaN;
    return this.sizeUnit === 'cm'
      ? Number.parseFloat(normalized)
      : Number.parseInt(normalized, 10);
  }
  fractionToDecimal(fraction = '-/-') {
    if (!fraction || ['-/-', '0/0'].includes(fraction)) return 0;
    const [numerator, denominator] = fraction.split('/').map(Number);
    if (!numerator || !denominator) return 0;
    return numerator / denominator;
  }
  formatEighthFraction(eighths = 0) {
    const fractionMap = {
      0: '0/0',
      1: '1/8',
      2: '1/4',
      3: '3/8',
      4: '1/2',
      5: '5/8',
      6: '3/4',
      7: '7/8'
    };
    return fractionMap[eighths] || '-/-';
  }
  setFractionSelectValue(select, value = '-/-') {
    if (!select) return;
    select.querySelector('.tfs__option--selected')?.classList.remove('tfs__option--selected');
    const option = select.querySelector(`.tfs__option[data-label="${value}"]`) || select.querySelector('.tfs__option[data-label="-/-"]');
    option?.classList.add('tfs__option--selected');
  }
  inchesToCm(value, fraction = '-/-', step = 0) {
    if (!value || isNaN(value)) {
      return {
        value: ''
      };
    }
    const inches = Number(value) + this.fractionToDecimal(fraction);
    let _value = Math.round(inches * 2.54 * 10) / 10;
    if (step === 0) {
      _value = Math.round(inches * 2.54);
    }
    return { value: _value };
  }
  cmToInches(value, type = 0) {
    if (!value || isNaN(value)) {
      return {
        value: '',
        fraction: '-/-'
      };
    }
    if (type === 0 || !this.allowFraction) {
      return {
        value: Math.round(Number(value) / 2.54),
        fraction: '-/-'
      };
    }
    const totalEighths = Math.round((Number(value) / 2.54) * 8);
    const integer = Math.floor(totalEighths / 8);
    const fraction = this.formatEighthFraction(totalEighths % 8);
    return {
      value: integer,
      fraction
    };
  }
  cmToInchesDecimal(value, precision = 4) {
    if (!value || isNaN(value)) return '';
    return Number((Number(value) / 2.54).toFixed(precision));
  }
  getFractionMaxOffset() {
    return this.allowFraction ? 7 / 8 : 0;
  }
  getMaxComparableValue(value) {
    const numericValue = Number(value);
    if (isNaN(numericValue)) return numericValue;
    return numericValue + this.getFractionMaxOffset();
  }
  formatInchesRuleValue(value) {
    return Math.floor(Number(value));
  }
  toComparableInches(value = '') {
    const normalized = this.toComparableNumber(value);
    if (isNaN(normalized)) return NaN;
    if (this.sizeUnit !== 'cm') return normalized;
    return this.cmToInchesDecimal(normalized, 4);
  }
  formatRuleValue(value) {
    const numericValue = Number(value);
    if (isNaN(numericValue)) return value;
    return this.sizeUnit === 'cm'
      ? this.inchesToCm(numericValue, '-/-', 1).value
      : this.formatInchesRuleValue(numericValue);
  }
  switchUnitValidate() {
    const errorTipNumbers = this.querySelectorAll('.variant-error-tip span[data-number]');
    for (const num of errorTipNumbers) {
      num.textContent = this.formatRuleValue(num.dataset.number);
    }
  }
  switchUnit(newUnit) {
    const unit = newUnit.dataset.unit;
    if (newUnit.classList.contains('tps-unit-btn--active')) return;
    this.sizeUnit = unit;
    this.querySelectorAll('.tps-size-field-fraction').forEach(el => {
      el.style.display = unit === 'cm' ? 'none' : '';
    });
    this.unitLabels.forEach(label => { label.textContent = unit; });
    this.querySelector('.tps-unit-btn.tps-unit-btn--active').classList.remove('tps-unit-btn--active');
    newUnit.classList.add('tps-unit-btn--active');
    if (unit === 'cm') {
      const width = this.inchesToCm(this.widthInputValue, this.widthFractionSelectValue, 1);
      const length = this.inchesToCm(this.lengthInputValue, this.lengthFractionSelectValue, 1);
      this.widthInputValue = width.value;
      this.lengthInputValue = length.value;
      if (this.allowFraction) {
        const widthWrapper = this.widthFractionSelect.closest('.tps-size-field');
        const lengthWrapper = this.lengthFractionSelect.closest('.tps-size-field');
        this.removeFractionError(widthWrapper);
        this.removeFractionError(lengthWrapper);
      }
    } else {
      const width = this.cmToInches(this.widthInputValue, 1);
      const length = this.cmToInches(this.lengthInputValue, 1);
      this.widthInputValue = width.value;
      this.lengthInputValue = length.value;
    }
    this.switchUnitValidate();
    this.dispatchEvent(new CustomEvent('update-field-size', { bubbles: true }));
  }
  createError(text, field) {
    const input = field.querySelector('.tps-size-field-input input');
    TpsFieldDateA11y.showFieldError(input, field, text, { allowHtml: true });
  }
  removeError(field) {
    const input = field.querySelector('.tps-size-field-input input');
    if (!input) return;
    TpsFieldDateA11y.clearFieldError(input, `${input.id || input.name}-error`);
  }
  createFractionError(text, field) {
    const input = field.querySelector('tps-field-select input');
    TpsFieldDateA11y.showFieldError(input, field, text, {
      className: 'variant-fraction-error-tip'
    });
  }
  removeFractionError(field) {
    const input = field.querySelector('tps-field-select input');
    if (!input) return;
    TpsFieldDateA11y.clearFieldError(input, `${input.id || input.name}-error`);
  }
  connectedCallback() {
    for (const sizeInput of this.sizeInputs) {
      let isComposing = false;
      sizeInput.addEventListener('compositionstart', () => {
        isComposing = true;
      });
      sizeInput.addEventListener('compositionend', (e) => {
        isComposing = false;
        this.formatInput(e.target.dataset.type);
      });
      sizeInput.addEventListener('input', (e) => {
        if (isComposing) return;
        this.formatInput(e.target.dataset.type);
      });
      sizeInput.addEventListener('wheel', (e) => {
        e.target.blur();
      });
    }
    this.addEventListener('update-field-size-select', ({ detail }) => {
      this.fractionValidateRule(detail?.type);
      this.dispatchEvent(new CustomEvent('update-field-size', { bubbles: true }));
    });
    if (this.enableCm) {
      for (const unitBtn of this.unitBtns) {
        unitBtn.addEventListener('click', () => this.switchUnit(unitBtn));
      }
    }
  }
  formatInput(type) {
    const { tps_width_value, tps_length_value } = this.validateRule(type);
    if (this.shouldRewriteInput(tps_width_value)) {
      this.widthInputValue = this.toComparableNumber(tps_width_value);
    }
    if (this.shouldRewriteInput(tps_length_value)) {
      this.lengthInputValue = this.toComparableNumber(tps_length_value);
    }
    if (this.sizeUnit === 'cm' && this.allowFraction) {
      const width = this.cmToInches(this.widthInputValue, 1);
      const length = this.cmToInches(this.lengthInputValue, 1);
      this.widthFractionSelectValue = width.fraction;
      this.lengthFractionSelectValue = length.fraction;
    }
    this.dispatchEvent(new CustomEvent('update-field-size', { bubbles: true }));
  }
  validateRule(type) {
    const minWidth = Number(this.sizeWidth.dataset.min);
    const maxWidth = Number(this.sizeWidth.dataset.max);
    const minLength = Number(this.sizeLength.dataset.min);
    const maxLength = Number(this.sizeLength.dataset.max);
    const widthValue = this.sizeWidth.value;
    const lengthValue = this.sizeLength.value;

    const _width = this.toComparableInches(widthValue);
    const _length = this.toComparableInches(lengthValue);
    
    const widthWrapper = this.sizeWidth.closest('.tps-size-field');
    const lengthWrapper = this.sizeLength.closest('.tps-size-field');
    const baseRules = {
      width: { min: minWidth, max: this.getMaxComparableValue(maxWidth), wrapper: widthWrapper },
      length: { min: minLength, max: this.getMaxComparableValue(maxLength), wrapper: lengthWrapper },
    };
    const value = type === "width" ? _width : _length;
    const { min, max, wrapper } = baseRules[type] ?? {};
    if (isNaN(value)) {
      if (this.required) {
        const message = 'Please fill in field';
        this.createError(message, wrapper);
        return {
          ok: false,
          message,
          tps_width_value: widthValue,
          tps_length_value: lengthValue
        }
      }
    } else if (value < min || value > max) {
      const message = `${this.getDimensionLabel(type)} must be between <span data-number="${min}">${this.formatRuleValue(min)}</span> and <span data-number="${max}">${this.formatRuleValue(max)}</span>`;
      this.createError(message, wrapper);
      return {
        ok: false,
        message,
        tps_width_value: widthValue,
        tps_length_value: lengthValue
      }
    } else {
      const { constraints = [] } = this.sizeValidationRule?.validationRules || {};
      const result = this.validate(constraints, _length, _width, type);
      if (!result.ok) {
        this.createError(result.message, wrapper);
        return {
          ok: false,
          message: result.message,
          tps_width_value: widthValue,
          tps_length_value: lengthValue
        }
      } else {
        if (result.type === 'all') {
          this.removeError(widthWrapper);
          this.removeError(lengthWrapper);
        } else {
          this.removeError(wrapper);
        }
        return {
          ok: true,
          tps_width_value: widthValue,
          tps_length_value: lengthValue
        }
      }
    }
  }
  fractionValidateRule(type = 'width') {
    if (!this.allowFraction || this.sizeUnit === 'cm') return { ok: true };
    const widthWrapper = this.widthFractionSelect.closest('.tps-size-field');
    const lengthWrapper = this.lengthFractionSelect.closest('.tps-size-field');
    const baseRules = {
      width: { wrapper: widthWrapper, value: this.widthFractionSelectValue },
      length: { wrapper: lengthWrapper, value: this.lengthFractionSelectValue },
    };
    const { wrapper, value } = baseRules[type];
    if (value === '-/-') {
      this.createFractionError('Please select a fraction.', wrapper);
      return {
        ok: false,
        message: 'Please select a fraction.'
      }
    } else {
      this.removeFractionError(wrapper);
      return {
        ok: true
      }
    }
  }
  validate(rules, length, width, changedField) {
    const values = { length, width };
    const hitRule = rules.some((rule) => {
      const { whenOk, thenOk } = this.returnValue(rule, values);
      return whenOk && thenOk
    });
    if (hitRule) {
      return { ok: true, type: 'all' };
    }
    for (const rule of rules) {
      const { when, then, id } = rule;
      const { whenOk, thenOk, whenDim, thenDim } = this.returnValue(rule, values);
      if (changedField === thenDim && whenOk && !thenOk) {
        return {
          ok: false,
          ruleId: id,
          message: this._buildMessage(then, changedField)
        };
      }
      if (changedField === whenDim && thenOk && !whenOk) {
        return {
          ok: false,
          ruleId: id,
          message: this._buildMessage(when, changedField)
        };
      }
    }
    return { ok: true };
  } 
  returnValue(rule, values) {
    const { when, then } = rule;
    const whenDim = when.dimension;
    const thenDim = then.dimension;
    const whenValue = values[whenDim];
    const thenValue = values[thenDim];
    const whenOk = this.checkCondition(when, whenValue);
    const thenOk = this.checkCondition(then, thenValue);
    return { whenOk, thenOk, thenDim, whenDim };
  }
  checkCondition(cond, val) {
    if (cond.comparator === "between") {
      const min = Number(cond.min);
      const max = this.getMaxComparableValue(cond.max);
      return val >= min && val <= max;
    } 
    if (cond.comparator === "equals") {
      const value = Number(cond.value);
      return val === value;
    }
    return false;
  }
  _buildMessage(rulePart, field) {
    const label = this.getDimensionLabel(field);
    if (rulePart.comparator === "between") {
      const min = Number(rulePart.min);
      const max = this.getMaxComparableValue(rulePart.max);
      return `${label} must be between <span data-number="${min}">${this.formatRuleValue(min)}</span> and <span data-number="${max}">${this.formatRuleValue(max)}</span>.`;
    }
    if (rulePart.comparator === "equals") {
      const value = Number(rulePart.value);
      return `${label} must be equal to <span data-number="${value}">${this.formatRuleValue(value)}</span>.`;
    }
    return `${label} does not meet the requirements.`;
  }
}
customElements.define('tps-field-size', TpsFieldSize);

class TpsFieldSelect extends HTMLElement {
  constructor() {
    super();
    this.required = this.dataset.required === 'true';
    this.allowMultiple = this.dataset.allowMultiple === 'true';
    this.selectType = this.dataset.selectType || 1;
    this.input = this.querySelector('input');
    this.optionsContainer = this.querySelector('.tfs__options');
    this.options = this.querySelectorAll('.tfs__options li');
    this.field = this.closest('div');
    this.icon = this.querySelector('.tfs-icon');
    this.errorId = `${this.input.id || this.input.name}-error`;
    this.error = null;
    this.activeOption = null;
    this.hideTimer = null;
  }
  get optionsHeight() {
    return this.optionsContainer.scrollHeight;
  }
  get availableOptions() {
    return [...this.options].filter(option =>
      option.dataset.key !== '-/-' &&
      option.getAttribute('aria-disabled') !== 'true' &&
      option.getAttribute('aria-hidden') !== 'true'
    );
  }
  get value() {
    const checked = this.querySelectorAll('li.tfs__option--selected');
    return [...checked].map(({ dataset }) => ({ key: dataset.key, value: dataset.label }));
  }
  set value(value) {
    this.input.value = value;
    this.syncSelectedState();
  }
  get expanded() {
    return this.input.getAttribute('aria-expanded') === 'true';
  }
  openOptions() {
    clearTimeout(this.hideTimer);
    this.optionsContainer.style.display = 'block';
    this.input.setAttribute('aria-expanded', 'true');
    const selectedOption = this.querySelector('.tfs__option--selected');
    this.setActiveOption(this.availableOptions.includes(selectedOption) ? selectedOption : null);
    setTimeout(() => {
      this.optionsContainer.style.height = `${this.optionsHeight}px`;
    });
  }
  hideOptions() {
    clearTimeout(this.hideTimer);
    this.optionsContainer.style.height = '0';
    this.input.setAttribute('aria-expanded', 'false');
    this.setActiveOption(null);
    this.hideTimer = setTimeout(() => {
      this.optionsContainer.style.display = 'none';
    }, 300);
  }
  setActiveOption(option) {
    this.activeOption?.classList.remove('tfs__option--active');
    this.activeOption = option;
    if (!option) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }
    option.classList.add('tfs__option--active');
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }
  moveActiveOption(step) {
    const options = this.availableOptions;
    if (!options.length) return;
    const activeIndex = options.indexOf(this.activeOption);
    const nextIndex = activeIndex < 0
      ? (step > 0 ? 0 : options.length - 1)
      : (activeIndex + step + options.length) % options.length;
    this.setActiveOption(options[nextIndex]);
  }
  syncSelectedState() {
    for (const option of this.options) {
      option.setAttribute('aria-selected', `${option.classList.contains('tfs__option--selected')}`);
    }
  }
  clearError() {
    this.error?.remove();
    this.error = null;
    const descriptions = (this.input.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter(id => id && id !== this.errorId);
    if (descriptions.length) {
      this.input.setAttribute('aria-describedby', descriptions.join(' '));
    } else {
      this.input.removeAttribute('aria-describedby');
    }
    this.input.setAttribute('aria-invalid', 'false');
  }
  createError(text) {
    this.clearError();
    this.error = document.createElement('div');
    this.error.id = this.errorId;
    this.error.className = 'variant-error-tip';
    this.error.setAttribute('role', 'alert');
    this.error.textContent = text;
    this.field.prepend(this.error);
    const descriptions = new Set((this.input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    descriptions.add(this.errorId);
    this.input.setAttribute('aria-describedby', [...descriptions].join(' '));
    this.input.setAttribute('aria-invalid', 'true');
  }
  validateRule() {
    const value = this.input.value;
    const valid = Array.from(this.querySelectorAll('li.tfs__option--selected')).filter(
      li => li.getAttribute('aria-disabled') !== 'true' && li.getAttribute('aria-hidden') !== 'true'
    )
    if (this.required && (!value || valid.length <= 0)) {
      const message = 'Please select in field';
      this.createError(message);
      return {
        ok: false,
        value,
        message
      }
    }
    this.clearError();
    return {
      ok: true,
      value
    }
  }
  selectOption(option) {
    if (!this.availableOptions.includes(option)) return;
    this.hideOptions();
    if (option.classList.contains('tfs__option--selected')) return;
    this.optionsContainer.querySelector('.tfs__option--selected')?.classList.remove('tfs__option--selected');
    option.classList.add('tfs__option--selected');
    this.value = this.value.map(({ value }) => value).join(',');
    this.clearError();
    if (this.selectType == 1) {
      this.dispatchEvent(new CustomEvent('update-field-checked', {
        bubbles: true
      }));
    }
    if (['width', 'length'].includes(this.selectType))  {
      this.dispatchEvent(new CustomEvent('update-field-size-select', {
        detail: { type: this.selectType },
        bubbles: true
      }));
    }
  }
  handleKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.expanded) this.openOptions();
      this.moveActiveOption(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (this.expanded && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault();
      const options = this.availableOptions;
      this.setActiveOption(event.key === 'Home' ? options[0] : options[options.length - 1]);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.expanded && this.activeOption) {
        this.selectOption(this.activeOption);
      } else {
        this.openOptions();
      }
      return;
    }
    if (event.key === 'Escape' && this.expanded) {
      event.preventDefault();
      this.hideOptions();
      return;
    }
    if (event.key === 'Tab') this.hideOptions();
  }
  connectedCallback() {
    this.input.setAttribute('aria-required', `${this.required}`);
    this.syncSelectedState();
    this.input.addEventListener('focus', () => this.openOptions());
    this.input.addEventListener('click', () => this.openOptions());
    this.input.addEventListener('keydown', event => this.handleKeydown(event));
    this.input.addEventListener('blur', (event) => {
      if (event.relatedTarget && event.relatedTarget?.closest('tps-field-select') === this.input.closest('tps-field-select')) return;
      this.hideOptions();
    });
    this.icon?.addEventListener('click', () => {
      this.input.focus();
      this.openOptions();
    });
    for (const option of this.options) {
      option.addEventListener('mousedown', event => event.preventDefault());
      option.addEventListener('click', () => {
        this.selectOption(option);
      });
    }
  }
}
customElements.define('tps-field-select', TpsFieldSelect)

class TpsFieldCheckbox extends HTMLElement {
  constructor() {
    super();
    this.required = this.dataset.required || false;
    this.allowMultiple = this.dataset.allowMultiple || false;
    this.minMultiple = this.dataset.minMultiple;
    this.maxMultiple = this.dataset.maxMultiple;
    this.field = this.closest('.tps-fields');
    this.error = null;
    this.errorTimer = null;
  }
  get value() {
    const checked = this.querySelectorAll('input:checked');
    return [...checked].map(({ value, dataset }) => ({ key: dataset.key, value }));
  }
  get checkedCount() {
    return Array.from(this.querySelectorAll('input:checked')).filter(
      input => input.getAttribute('aria-disabled') !== 'true' && input.getAttribute('aria-hidden') !== 'true'
    ).length;
  }
  get inputs() {
    return [...this.querySelectorAll('input')];
  }
  get availableInputs() {
    return this.inputs.filter(
      input => !input.disabled && input.getAttribute('aria-disabled') !== 'true' && input.getAttribute('aria-hidden') !== 'true'
    );
  }
  clearError() {
    clearTimeout(this.errorTimer);
    const errorId = this.error?.id;
    for (const input of this.inputs) {
      TpsFieldDateA11y.clearFieldError(input, errorId || `${input.id || input.name}-error`);
    }
    this.error = null;
  }
  createError(text, hide) {
    this.clearError();
    const [control, ...otherControls] = this.availableInputs;
    if (!control) return;
    const result = TpsFieldDateA11y.showFieldError(control, this.field, text, { prepend: true });
    this.error = result.error;
    for (const input of otherControls) {
      TpsFieldDateA11y.setFieldErrorState(input, result.errorId, true);
    }
    if (!hide) return;
    this.errorTimer = setTimeout(() => {
      this.clearError();
    }, 3000);
  }
  validateRule() {
    if (this.required && this.checkedCount === 0) {
      const message = 'Please select at least one option';
      this.createError(message);
      return {
        ok: false,
        message
      }
    }
    if (this.allowMultiple) {
      if (this.checkedCount < this.minMultiple) {
        const message = `Please select at least ${this.minMultiple} options`;
        this.createError(message);
        return {
          ok: false,
          message
        }
      }
    }
    this.clearError();
    return {
      ok: true
    }
  }
  checkedRule() {
    this.addEventListener('change', (event) => {
      if (this.required && this.allowMultiple) {
        if (this.checkedCount > this.maxMultiple) {
          event.target.checked = false;
          this.createError(`No more than ${this.maxMultiple} at most`, true);
          return;
        }
      }
      this.clearError();
      this.dispatchEvent(new CustomEvent('update-field-checked', { bubbles: true }));
    });
  }
}
class TpsFieldButton extends TpsFieldCheckbox {
  constructor() {
    super();
  }
  connectedCallback() {
    this.checkedRule();
  }
}
customElements.define('tps-field-button', TpsFieldButton);
class TpsFieldImage extends TpsFieldCheckbox {
  constructor() {
    super();
  }
  connectedCallback() {
    this.checkedRule();
  }
}
customElements.define('tps-field-image', TpsFieldImage);

class TpsFieldUpload extends HTMLElement {
  constructor() {
    super();
    this.required = this.dataset.required || false;
    this.input = this.querySelector('input');
    this.field = this.closest('.tps-fields');
    this.error = null;
  }
  createError(text) {
    this.clearError();
    const result = TpsFieldDateA11y.showFieldError(this.input, this.field, text, { prepend: true });
    this.error = result.error;
  }
  clearError() {
    const errorId = this.error?.id || `${this.input.id || this.input.name}-error`;
    TpsFieldDateA11y.clearFieldError(this.input, errorId);
    this.error = null;
  }
  validateRule() {
    if (this.required && !this.input.files.length) {
      const message = 'Please upload one file';
      this.createError(message);
      return {
        ok: false,
        message
      }
    }
    this.clearError();
    return {
      ok: true
    }
  }
  connectedCallback() {
    this.input.addEventListener('change', (e) => {
      const files = e.target.files
      if (files && files.length > 0) {
        this.clearError();
      }
    });
  }
}
customElements.define('tps-field-upload', TpsFieldUpload);

class TpsFieldRadio extends TpsFieldCheckbox {
  constructor() {
    super();
  }
  connectedCallback() {
    this.checkedRule();
  }
}
customElements.define('tps-field-radio', TpsFieldRadio);
